// \src\pages\admin\dashboard\page.tsx

import { useState, useEffect, useCallback, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  useAdmin,
  type Banner,
  type Brand,
  type Category,
  type Product,
  type Seller,
  type NewsItem,
  type Store,
  type Testimonial,
  type CompanyTimelineItem,
  type OutletPage,
  type JobVacancy,
  type Raffle,
  type RaffleField,
  loadBackups,
  saveBackup,
  restoreBackup,
  autoSaveBackup,
  type AdminBackup,
} from "@/context/AdminContext";

type Section =
  | "settings"
  | "banners"
  | "brands"
  | "categories"
  | "products"
  | "sellers"
  | "news"
  | "stores"
  | "outlet"
  | "company"
  | "promotions"
  | "testimonials"
  | "forms"
  | "vacancies"
  | "raffles"
  | "backups";

const sectionLabels: Record<Section, string> = {
  settings: "Configurações",
  banners: "Banners",
  brands: "Marcas",
  categories: "Categorias",
  products: "Produtos",
  sellers: "Vendedores",
  news: "Notícias",
  stores: "Lojas",
  outlet: "MZ Outlet",
  company: "A Empresa / Trajetória",
  promotions: "Promoções",
  testimonials: "Depoimentos",
  forms: "Formulários Recebidos",
  vacancies: "Trabalhe Conosco",
  raffles: "Sorteios",
  backups: "Backups",
};

const sectionIcons: Record<Section, string> = {
  settings: "ri-settings-3-line",
  banners: "ri-image-line",
  brands: "ri-building-4-line",
  categories: "ri-grid-line",
  products: "ri-shopping-bag-3-line",
  sellers: "ri-team-line",
  news: "ri-newspaper-line",
  stores: "ri-store-2-line",
  outlet: "ri-store-3-line",
  company: "ri-building-line",
  promotions: "ri-price-tag-3-line",
  testimonials: "ri-chat-quote-line",
  forms: "ri-mail-open-line",
  vacancies: "ri-briefcase-4-line",
  raffles: "ri-gift-line",
  backups: "ri-database-2-line",
};

function newId(items: { id: number }[]): number {
  return items.length > 0 ? Math.max(...items.map(i => i.id)) + 1 : 1;
}

const storeOptions = [
  { value: "central", label: "Loja Central" },
  { value: "filial", label: "MZ Outlet" },
];

interface RaffleEntryValueAdmin {
  fieldId: number;
  label: string;
  fieldType: string;
  showOnTicket: boolean;
  value: string;
}

interface RaffleEntryAdmin {
  id: number;
  raffleId: number;
  name: string;
  cpf: string;
  email: string;
  whatsapp: string;
  answer: string;
  createdAt: string;
  values: RaffleEntryValueAdmin[];
}

interface ContactMessageAdmin {
  id: number;
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
  status: string;
  createdAt: string;
}

interface JobApplicationAdmin {
  id: number;
  name: string;
  email: string;
  phone: string;
  position: string;
  experience: string;
  resumeSummary: string;
  resumeFile: string;
  status: string;
  createdAt: string;
}

const MAX_IMAGE_SIZE = 10 * 1024 * 1024;
const MAX_BANNER_MEDIA_SIZE = 50 * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const ACCEPTED_BANNER_MEDIA_TYPES = [
  ...ACCEPTED_IMAGE_TYPES,
  "image/gif",
  "video/mp4",
  "video/webm",
];

function isVideoMedia(src: string): boolean {
  return /\.(mp4|webm)(?:[?#].*)?$/i.test(String(src || ""));
}

function todayString(): string {
  const today = new Date();
  return [
    today.getFullYear(),
    String(today.getMonth() + 1).padStart(2, "0"),
    String(today.getDate()).padStart(2, "0"),
  ].join("-");
}

function makeProductSlug(value: string): string {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/&/g, "-e-")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-+/g, "-");
}

function formatReceivedDate(value: string): string {
  if (!value) return "";

  const normalized = value.includes("T") ? value : value.replace(" ", "T");
  const date = new Date(normalized);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString("pt-BR");
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const {
    data,
    refreshData,
    updateSettings,
    setBanners,
    setBrands,
    setCategories,
    setProducts,
    setSellers,
    setNews,
    setRssSources,
    setStores,
    setCompanyInfo,
    setCompanyTimeline,
    setOutletPage,
    setOutletProducts,
    setPromotions,
    setTestimonials,
    setJobVacancies,
    setRaffles,
    resetAll,
  } = useAdmin();

  const [section, setSection] = useState<Section>("settings");
  const [editing, setEditing] = useState<any>(null);
  const [editType, setEditType] = useState<string>("");
  const [editOrigin, setEditOrigin] = useState<"" | "products" | "promotions">("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [msgType, setMsgType] = useState<"success" | "error">("success");
  const [backups, setBackups] = useState<AdminBackup[]>([]);
  const [autoBackupEnabled] = useState(true);
  const [authChecking, setAuthChecking] = useState(true);
  const [settingsDraft, setSettingsDraft] = useState(data.settings);
  const [settingsDirty, setSettingsDirty] = useState(false);
  const [productSearch, setProductSearch] = useState("");
  const [promotionSearch, setPromotionSearch] = useState("");
  const [productsDraft, setProductsDraft] = useState<Product[]>(data.products);
  const [productsDirty, setProductsDirty] = useState(false);
  const [promotionsDraft, setPromotionsDraft] = useState<number[]>(data.promotions);
  const [promotionDatesDraft, setPromotionDatesDraft] = useState<Record<number, string>>({});
  const [promotionsDirty, setPromotionsDirty] = useState(false);
  const [uploadingKey, setUploadingKey] = useState("");
  const [confirmDialog, setConfirmDialog] = useState<{
    title: string;
    message: string;
    confirmText: string;
    danger?: boolean;
    onConfirm: () => void | Promise<void>;
  } | null>(null);
  const [raffleManagerId, setRaffleManagerId] = useState<number | null>(null);
  const [raffleEntries, setRaffleEntries] = useState<RaffleEntryAdmin[]>([]);
  const [raffleEntriesTotal, setRaffleEntriesTotal] = useState(0);
  const [raffleEntrySearch, setRaffleEntrySearch] = useState("");
  const [raffleEntriesLoading, setRaffleEntriesLoading] = useState(false);
  const [raffleAdminWinners, setRaffleAdminWinners] = useState<Raffle["winners"]>([]);
  const [winnerPrizeByEntry, setWinnerPrizeByEntry] = useState<Record<number, string>>({});
  const [formsTab, setFormsTab] = useState<"contact" | "rh">("contact");
  const [formSearch, setFormSearch] = useState("");
  const [contactMessages, setContactMessages] = useState<ContactMessageAdmin[]>([]);
  const [contactMessagesTotal, setContactMessagesTotal] = useState(0);
  const [jobApplications, setJobApplications] = useState<JobApplicationAdmin[]>([]);
  const [jobApplicationsTotal, setJobApplicationsTotal] = useState(0);
  const [formsLoading, setFormsLoading] = useState(false);
  const [outletDraft, setOutletDraft] = useState<OutletPage>(data.outletPage);
  const [outletProductsDraft, setOutletProductsDraft] = useState<number[]>(data.outletProducts);
  const [outletProductSearch, setOutletProductSearch] = useState("");
  const [outletDirty, setOutletDirty] = useState(false);

  useEffect(() => {
    let active = true;

    const checkSession = async () => {
      try {
        const response = await fetch("/api/session.php", {
          method: "GET",
          credentials: "include",
          headers: {
            Accept: "application/json",
          },
        });

        const result = await response.json();

        if (!active) return;

        if (!response.ok || result?.authenticated !== true) {
          navigate("/admin/login", { replace: true });
          return;
        }

        setAuthChecking(false);
      } catch {
        if (!active) return;
        navigate("/admin/login", { replace: true });
      }
    };

    void checkSession();

    return () => {
      active = false;
    };
  }, [navigate]);

  const showMsg = (text: string, type: "success" | "error" = "success") => {
    setMsg(text);
    setMsgType(type);
    window.setTimeout(() => setMsg(""), 3000);
  };

  const loadRaffleEntries = useCallback(async (raffleId: number, search = "") => {
    setRaffleEntriesLoading(true);

    try {
      const params = new URLSearchParams({
        action: "entries",
        raffle_id: String(raffleId),
      });

      if (search.trim()) params.set("search", search.trim());

      const response = await fetch(`/api/sorteio-admin.php?${params.toString()}`, {
        method: "GET",
        credentials: "include",
        cache: "no-store",
        headers: { Accept: "application/json" },
      });
      const result = await response.json();

      if (!response.ok || result?.success !== true) {
        throw new Error(result?.message || "Não foi possível carregar os concorrentes.");
      }

      setRaffleEntries(Array.isArray(result.entries) ? result.entries : []);
      setRaffleEntriesTotal(Number(result.total) || 0);
      setRaffleAdminWinners(Array.isArray(result.winners) ? result.winners : []);
    } catch (error) {
      setRaffleEntries([]);
      setRaffleEntriesTotal(0);
      setMsg(error instanceof Error ? error.message : "Não foi possível carregar os concorrentes.");
      setMsgType("error");
      window.setTimeout(() => setMsg(""), 3000);
    } finally {
      setRaffleEntriesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!raffleManagerId) return;

    const timer = window.setTimeout(() => {
      void loadRaffleEntries(raffleManagerId, raffleEntrySearch);
    }, 250);

    return () => window.clearTimeout(timer);
  }, [raffleManagerId, raffleEntrySearch, loadRaffleEntries]);

  const openRaffleManager = (raffleId: number) => {
    setRaffleManagerId(raffleId);
    setRaffleEntrySearch("");
    setRaffleEntries([]);
    setRaffleEntriesTotal(0);
    setRaffleAdminWinners([]);
    setWinnerPrizeByEntry({});
  };

  const closeRaffleManager = () => {
    setRaffleManagerId(null);
    setRaffleEntries([]);
    setRaffleEntriesTotal(0);
    setRaffleEntrySearch("");
    setRaffleAdminWinners([]);
    setWinnerPrizeByEntry({});
  };

  const loadReceivedForms = useCallback(async (type: "contact" | "rh", search = "") => {
    setFormsLoading(true);

    try {
      const params = new URLSearchParams({ type });

      if (search.trim()) {
        params.set("search", search.trim());
      }

      const response = await fetch(`/api/formularios-admin.php?${params.toString()}`, {
        method: "GET",
        credentials: "include",
        cache: "no-store",
        headers: { Accept: "application/json" },
      });
      const result = await response.json();

      if (!response.ok || result?.success !== true) {
        throw new Error(result?.message || "Não foi possível carregar os formulários recebidos.");
      }

      const entries = Array.isArray(result.entries) ? result.entries : [];
      const total = Number(result.total) || 0;

      if (type === "contact") {
        setContactMessages(entries);
        setContactMessagesTotal(total);
      } else {
        setJobApplications(entries);
        setJobApplicationsTotal(total);
      }
    } catch (error) {
      if (type === "contact") {
        setContactMessages([]);
        setContactMessagesTotal(0);
      } else {
        setJobApplications([]);
        setJobApplicationsTotal(0);
      }

      setMsg(error instanceof Error ? error.message : "Não foi possível carregar os formulários recebidos.");
      setMsgType("error");
      window.setTimeout(() => setMsg(""), 3000);
    } finally {
      setFormsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (section !== "forms") return;

    const timer = window.setTimeout(() => {
      void loadReceivedForms(formsTab, formSearch);
    }, 250);

    return () => window.clearTimeout(timer);
  }, [section, formsTab, formSearch, loadReceivedForms]);

  const deleteReceivedForm = async (type: "contact" | "rh", id: number) => {
    try {
      const response = await fetch("/api/formularios-admin.php", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          action: "delete",
          type,
          id,
        }),
      });
      const result = await response.json();

      if (!response.ok || result?.success !== true) {
        throw new Error(result?.message || "Não foi possível excluir o registro.");
      }

      showMsg("Registro excluído com sucesso!");
      await loadReceivedForms(type, formSearch);
    } catch (error) {
      showMsg(
        error instanceof Error ? error.message : "Não foi possível excluir o registro.",
        "error",
      );
    }
  };

  const requestConfirm = (
    title: string,
    message: string,
    onConfirm: () => void | Promise<void>,
    confirmText = "Confirmar",
    danger = false,
  ) => {
    setConfirmDialog({
      title,
      message,
      onConfirm,
      confirmText,
      danger,
    });
  };

  const refreshBackups = useCallback(async () => {
    try {
      const updated = await loadBackups();
      setBackups(updated);
    } catch (error) {
      setBackups([]);
      showMsg(
        error instanceof Error
          ? error.message
          : "Não foi possível carregar os backups.",
        "error",
      );
    }
  }, []);

  useEffect(() => {
    void refreshBackups();
  }, [refreshBackups]);

  useEffect(() => {
    if (!settingsDirty) {
      setSettingsDraft(data.settings);
    }
  }, [data.settings, settingsDirty]);

  useEffect(() => {
    if (!productsDirty) {
      setProductsDraft(data.products);
    }
  }, [data.products, productsDirty]);

  useEffect(() => {
    if (!promotionsDirty) {
      setPromotionsDraft(data.promotions);
      setPromotionDatesDraft(
        Object.fromEntries(
          data.products.map(product => [product.id, product.promotionUntil || ""]),
        ),
      );
    }
  }, [data.promotions, data.products, promotionsDirty]);

  useEffect(() => {
    if (!outletDirty) {
      setOutletDraft(data.outletPage);
      setOutletProductsDraft(data.outletProducts);
    }
  }, [data.outletPage, data.outletProducts, outletDirty]);

  const updateSettingsDraft = (
    field: keyof typeof settingsDraft,
    value: string,
  ) => {
    setSettingsDraft(prev => ({
      ...prev,
      [field]: value,
    }));
    setSettingsDirty(true);
  };

  const handleSaveSettings = () => {
    updateSettings(settingsDraft);
    setSettingsDirty(false);
    showMsg("Configurações salvas com sucesso!");
  };

  const handleLogout = async () => {
    try {
      await fetch("/api/logout.php", {
        method: "POST",
        credentials: "include",
        headers: {
          Accept: "application/json",
        },
      });
    } finally {
      navigate("/admin/login", { replace: true });
    }
  };

  const handleManualBackup = async () => {
    try {
      const updated = await saveBackup(
        data,
        `Manual ${new Date().toLocaleString("pt-BR")}`,
      );

      setBackups(updated);
      showMsg("Backup salvo com sucesso!");
    } catch (error) {
      showMsg(
        error instanceof Error
          ? error.message
          : "Não foi possível salvar o backup.",
        "error",
      );
    }
  };

  const handleAutoBackup = async () => {
    if (!autoBackupEnabled) return;

    try {
      const updated = await autoSaveBackup(data);
      setBackups(updated);
      showMsg("Auto-backup verificado com sucesso!");
    } catch (error) {
      showMsg(
        error instanceof Error
          ? error.message
          : "Não foi possível realizar o auto-backup.",
        "error",
      );
    }
  };

  const handleRestoreBackup = (backup: AdminBackup) => {
    requestConfirm(
      "Restaurar backup",
      `Restaurar ${backup.label}? Os dados atuais serão substituídos.`,
      async () => {
        try {
          await restoreBackup(backup);
          window.location.reload();
        } catch (error) {
          showMsg(
            error instanceof Error
              ? error.message
              : "Não foi possível restaurar o backup.",
            "error",
          );
        }
      },
      "Restaurar",
      true,
    );
  };

  const handleSaveProducts = () => {
    const validIds = new Set(productsDraft.map(product => product.id));
    const filteredPromotions = data.promotions.filter(id => validIds.has(id));

    setProducts(productsDraft);
    setPromotions(filteredPromotions);
    setPromotionsDraft(prev => prev.filter(id => validIds.has(id)));
    setProductsDirty(false);
    showMsg("Alterações dos produtos salvas com sucesso!");
  };

  const handleSavePromotions = () => {
    const productsWithDates = data.products.map(product => ({
      ...product,
      promotionUntil: promotionDatesDraft[product.id] || "",
    }));

    setProducts(productsWithDates);
    setPromotions(promotionsDraft);
    setPromotionsDirty(false);
    showMsg("Alterações das promoções salvas com sucesso!");
  };

  const updateProductDraft = (id: number, patch: Partial<Product>) => {
    setProductsDraft(prev => prev.map(product => (
      product.id === id ? { ...product, ...patch } : product
    )));
    setProductsDirty(true);
  };

  const deleteProductDraft = (id: number) => {
    requestConfirm(
      "Excluir produto",
      "Tem certeza que deseja excluir este produto? A exclusão será efetivada ao clicar em Salvar Alterações.",
      () => {
        setProductsDraft(prev => prev.filter(product => product.id !== id));
        setProductsDirty(true);
      },
      "Excluir",
      true,
    );
  };

  const togglePromotionDraft = (productId: number) => {
    setPromotionsDraft(prev => (
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    ));
    setPromotionsDirty(true);
  };

  const deletePromotionProduct = (productId: number) => {
    const product = data.products.find(item => item.id === productId);

    requestConfirm(
      "Excluir produto",
      product
        ? `Excluir "${product.name}"? O produto será removido também da página Produtos e de qualquer promoção vinculada.`
        : "Excluir este produto? Ele será removido também da página Produtos e de qualquer promoção vinculada.",
      () => {
        const nextProducts = data.products.filter(item => item.id !== productId);
        const nextPromotions = data.promotions.filter(id => id !== productId);

        setProducts(nextProducts);
        setProductsDraft(nextProducts);
        setProductsDirty(false);

        setPromotions(nextPromotions);
        setPromotionsDraft(nextPromotions);
        setPromotionsDirty(false);

        setPromotionDatesDraft(prev => {
          const next = { ...prev };
          delete next[productId];
          return next;
        });

        showMsg("Produto excluído com sucesso!");
      },
      "Excluir",
      true,
    );
  };

  const updatePromotionDate = (productId: number, value: string) => {
    setPromotionDatesDraft(prev => ({
      ...prev,
      [productId]: value,
    }));
    setPromotionsDirty(true);
  };

  const updateOutletDraft = <K extends keyof OutletPage,>(
    field: K,
    value: OutletPage[K],
  ) => {
    setOutletDraft(prev => ({
      ...prev,
      [field]: value,
    }));
    setOutletDirty(true);
  };

  const addOutletProduct = (productId: number) => {
    setOutletProductsDraft(prev => (
      prev.includes(productId) ? prev : [...prev, productId]
    ));
    setOutletDirty(true);
  };

  const removeOutletProduct = (productId: number) => {
    setOutletProductsDraft(prev => prev.filter(id => id !== productId));
    setOutletDirty(true);
  };

  const moveOutletProduct = (productId: number, direction: -1 | 1) => {
    setOutletProductsDraft(prev => {
      const index = prev.indexOf(productId);
      const target = index + direction;

      if (index < 0 || target < 0 || target >= prev.length) {
        return prev;
      }

      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
    setOutletDirty(true);
  };

  const handleSaveOutlet = () => {
    const validIds = new Set(data.products.map(product => product.id));
    const cleanedProductIds = outletProductsDraft.filter((id, index, list) => (
      validIds.has(id) && list.indexOf(id) === index
    ));

    setOutletPage(outletDraft);
    setOutletProducts(cleanedProductIds);
    setOutletProductsDraft(cleanedProductIds);
    setOutletDirty(false);
    showMsg("Página MZ Outlet salva com sucesso!");
  };

  const uploadImage = async (
    file: File,
    folder: string,
    key: string,
    allowBannerMedia = false,
  ): Promise<string | null> => {
    const allowedTypes = allowBannerMedia
      ? ACCEPTED_BANNER_MEDIA_TYPES
      : ACCEPTED_IMAGE_TYPES;

    if (!allowedTypes.includes(file.type)) {
      showMsg(
        allowBannerMedia
          ? "Use JPG, PNG, WebP, GIF, MP4 ou WebM."
          : "Use somente imagens JPG, PNG ou WebP.",
        "error",
      );
      return null;
    }

    const maxSize = allowBannerMedia && (file.type === "image/gif" || file.type.startsWith("video/"))
      ? MAX_BANNER_MEDIA_SIZE
      : MAX_IMAGE_SIZE;

    if (file.size > maxSize) {
      showMsg(
        maxSize === MAX_BANNER_MEDIA_SIZE
          ? "GIFs e vídeos podem ter no máximo 50 MB."
          : "A imagem ultrapassa o limite máximo de 10 MB.",
        "error",
      );
      return null;
    }

    setUploadingKey(key);

    try {
      const formData = new FormData();
      formData.append("image", file);
      formData.append("folder", folder);

      const response = await fetch("/api/upload-image.php", {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      const result = await response.json();

      if (!response.ok || result?.success !== true || !result?.path) {
        throw new Error(result?.message || "Não foi possível enviar a imagem.");
      }

      showMsg(allowBannerMedia ? "Mídia enviada com sucesso!" : "Imagem enviada e otimizada com sucesso!");
      return String(result.path);
    } catch (error) {
      showMsg(
        error instanceof Error ? error.message : "Não foi possível enviar a imagem.",
        "error",
      );
      return null;
    } finally {
      setUploadingKey("");
    }
  };

  const importImageFromUrl = async (
    url: string,
    folder: string,
    key: string,
    allowBannerMedia = false,
  ): Promise<string | null> => {
    const trimmedUrl = url.trim();

    if (!/^https?:\/\//i.test(trimmedUrl)) {
      showMsg("Cole um link completo começando com http:// ou https://.", "error");
      return null;
    }

    setUploadingKey(key);

    try {
      const response = await fetch("/api/upload-image.php", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ sourceUrl: trimmedUrl, folder }),
      });

      const result = await response.json();

      if (!response.ok || result?.success !== true || !result?.path) {
        throw new Error(result?.message || "Não foi possível importar essa imagem.");
      }

      showMsg(allowBannerMedia ? "Mídia importada com sucesso!" : "Imagem importada e otimizada com sucesso!");
      return String(result.path);
    } catch (error) {
      showMsg(
        error instanceof Error ? error.message : "Não foi possível importar essa imagem.",
        "error",
      );
      return null;
    } finally {
      setUploadingKey("");
    }
  };

  const postRaffleAdmin = async (payload: Record<string, unknown>) => {
    const response = await fetch("/api/sorteio-admin.php", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    });
    const result = await response.json();

    if (!response.ok || result?.success !== true) {
      throw new Error(result?.message || "Não foi possível concluir a operação do sorteio.");
    }

    return result;
  };

  const defineRaffleWinner = async (entry: RaffleEntryAdmin, prizeId: number) => {
    if (!raffleManagerId || prizeId <= 0) return;

    try {
      await postRaffleAdmin({
        action: "set_winner",
        raffle_id: raffleManagerId,
        prize_id: prizeId,
        entry_id: entry.id,
      });
      showMsg(`${entry.name} definido(a) como ganhador(a).`);
      await refreshData();
      await loadRaffleEntries(raffleManagerId, raffleEntrySearch);
    } catch (error) {
      showMsg(error instanceof Error ? error.message : "Não foi possível definir o ganhador.", "error");
    }
  };

  const handleDefineWinner = (entry: RaffleEntryAdmin) => {
    const prizeId = Number(winnerPrizeByEntry[entry.id] || 0);

    if (!prizeId) {
      showMsg("Selecione primeiro qual prêmio este concorrente ganhou.", "error");
      return;
    }

    const current = raffleAdminWinners.find(winner => Number(winner.prizeId) === prizeId);

    if (current && Number(current.entryId) !== entry.id) {
      requestConfirm(
        "Substituir ganhador",
        `O prêmio já está vinculado a ${current.name}. Deseja substituir pelo concorrente ${entry.name}?`,
        () => defineRaffleWinner(entry, prizeId),
        "Substituir",
        true,
      );
      return;
    }

    void defineRaffleWinner(entry, prizeId);
  };

  const handleWinnerPhotoUpload = async (winnerId: number, file?: File) => {
    if (!raffleManagerId || !file) return;

    const path = await uploadImage(file, "sorteios", `winner-photo-${winnerId}`);
    if (!path) return;

    try {
      await postRaffleAdmin({
        action: "winner_photo",
        raffle_id: raffleManagerId,
        winner_id: winnerId,
        photo: path,
      });
      showMsg("Foto do ganhador atualizada!");
      await refreshData();
      await loadRaffleEntries(raffleManagerId, raffleEntrySearch);
    } catch (error) {
      showMsg(error instanceof Error ? error.message : "Não foi possível atualizar a foto.", "error");
    }
  };

  const handleRemoveWinner = (winnerId: number, winnerName: string) => {
    if (!raffleManagerId) return;

    requestConfirm(
      "Desfazer ganhador",
      `Remover ${winnerName} da lista de ganhadores deste sorteio? O concorrente continuará normalmente na lista de participantes.`,
      async () => {
        try {
          await postRaffleAdmin({
            action: "remove_winner",
            raffle_id: raffleManagerId,
            winner_id: winnerId,
          });
          showMsg("Ganhador removido. O concorrente continua cadastrado.");
          await refreshData();
          await loadRaffleEntries(raffleManagerId, raffleEntrySearch);
        } catch (error) {
          showMsg(error instanceof Error ? error.message : "Não foi possível remover o ganhador.", "error");
        }
      },
      "Desfazer",
      true,
    );
  };

  const handleSettingsLogoUpload = async (file?: File) => {
    if (!file) return;
    const path = await uploadImage(file, "logo", "settings-logo");
    if (path) updateSettingsDraft("logoUrl", path);
  };

  const openEdit = (
    type: string,
    item?: any,
    origin: "" | "products" | "promotions" = "",
  ) => {
    setEditOrigin(origin);

    if (type === "banner") setEditing(item || { id: newId(data.banners), title: "", subtitle: "", description: "", ctaText: "Ver Mais", ctaLink: "/produtos", image: "" });
    else if (type === "brand") setEditing(item || { id: newId(data.brands), name: "", logo: "" });
    else if (type === "category") setEditing(item || { id: newId(data.categories), name: "", icon: "ri-building-2-line", image: "", subcategories: [] });
    else if (type === "product") {
      const sourceProducts = origin === "products" ? productsDraft : data.products;
      setEditing(item || { id: newId(sourceProducts), name: "", code: "", category: 0, subcategoryId: 0, brand: "", price: 0, showPrice: true, isPromo: false, promoLabel: "", description: "", specs: [""], images: [""], slug: "", active: true, activeUntil: "", promotionUntil: "" });
    }
    else if (type === "seller") setEditing(item || { id: newId(data.sellers), name: "", role: "Consultor de Vendas", phone: "", whatsapp: "", photo: "", available: true, inactiveUntil: "", store: "central" });
    else if (type === "sellerAvailability") setEditing({ ...item, available: false, inactiveUntil: item?.inactiveUntil || "" });
    else if (type === "newsItem") setEditing(item || { id: newId(data.news), title: "", excerpt: "", category: "", date: new Date().toISOString().split("T")[0], image: "", readTime: "3 min", source: "" });
    else if (type === "rssSource") setEditing(item || { id: newId(data.rssSources), name: "", url: "", enabled: true });
    else if (type === "store") setEditing(item || { id: newId(data.stores), name: "", address: "", city: "", cep: "", phone: "", whatsapp: "", hours: "", mapUrl: "", lat: -23.55, lng: -46.65, googleMapsUrl: "", wazeUrl: "" });
    else if (type === "company") setEditing({ ...data.companyInfo });
    else if (type === "companyConstruSete") setEditing({ ...data.companyInfo });
    else if (type === "companyTimeline") setEditing(item || {
      id: newId(data.companyTimeline),
      eventYear: new Date().getFullYear(),
      title: "",
      description: "",
      sortOrder: data.companyTimeline.length,
    });
    else if (type === "testimonial") setEditing(item || { id: newId(data.testimonials), name: "", text: "", stars: 5, photo: "" });
    else if (type === "jobVacancy") setEditing(item || { id: newId(data.jobVacancies), title: "", type: "", description: "", urgent: false, active: true });
    else if (type === "raffle") setEditing(item || { id: newId(data.raffles), title: "", description: "", bannerImage: "", question: "", regulation: "", startDate: "", endDate: "", active: true, visibleAfterEnd: true, fields: [], prizes: [{ id: 1, title: "", description: "", image: "" }], winners: [] });

    setEditType(type);
  };

  const closeEdit = () => {
    setEditing(null);
    setEditType("");
    setEditOrigin("");
  };

  const handleSave = () => {
    if (!editing) return;

    if ((editType === "seller" || editType === "sellerAvailability") && editing.available === false && !editing.inactiveUntil) {
      showMsg("Informe a data em que o vendedor voltará a ficar ativo.", "error");
      return;
    }

    if (editType === "product") {
      if (!String(editing.code || "").trim()) {
        showMsg("Informe o código interno do produto.", "error");
        return;
      }

      const selectedCategory = data.categories.find(
        category => category.id === Number(editing.category),
      );

      if (!selectedCategory) {
        showMsg("Selecione a categoria do produto.", "error");
        return;
      }

      const selectedSubcategory = selectedCategory.subcategories.find(
        subcategory => subcategory.id === Number(editing.subcategoryId),
      );

      if (!selectedSubcategory) {
        showMsg("Selecione a subcategoria do produto.", "error");
        return;
      }

      const slugBase = makeProductSlug(
        String(editing.slug || editing.name || ""),
      );

      if (!slugBase) {
        showMsg("Informe um nome válido para o produto.", "error");
        return;
      }

      const sourceProducts =
        editOrigin === "promotions"
          ? data.products
          : productsDraft;

      let finalSlug = slugBase;

      if (
        sourceProducts.some(
          item =>
            item.id !== editing.id &&
            String(item.slug || "").trim() === finalSlug,
        )
      ) {
        finalSlug = `${slugBase}-${editing.id}`;
      }

      editing.slug = finalSlug;
    }

    if (editType === "raffle") {
      if (String(editing.title || "").trim().length < 3) {
        showMsg("Informe um título válido para o sorteio.", "error");
        return;
      }

      if (editing.startDate && editing.endDate && editing.endDate < editing.startDate) {
        showMsg("A data final do sorteio não pode ser anterior à data inicial.", "error");
        return;
      }

      if (!Array.isArray(editing.prizes) || editing.prizes.length === 0) {
        showMsg("Cadastre pelo menos um item/prêmio para o sorteio.", "error");
        return;
      }

      if (editing.prizes.some((prize: any) => !String(prize.title || "").trim())) {
        showMsg("Todos os prêmios precisam ter um nome.", "error");
        return;
      }

      const prizeIds = new Set(editing.prizes.map((prize: any) => Number(prize.id)));
      const winnerWithRemovedPrize = (editing.winners || []).find((winner: any) => Number(winner.prizeId) > 0 && !prizeIds.has(Number(winner.prizeId)));

      if (winnerWithRemovedPrize) {
        showMsg("Um prêmio que já possui ganhador não pode ser removido. Desfaça o ganhador primeiro na área de Concorrentes.", "error");
        return;
      }

      const activeFields = Array.isArray(editing.fields) ? editing.fields : [];
      const labels = activeFields.map((field: any) => String(field.label || "").trim().toLowerCase()).filter(Boolean);

      if (activeFields.some((field: any) => !String(field.label || "").trim())) {
        showMsg("Todos os campos personalizados precisam ter um nome.", "error");
        return;
      }

      if (new Set(labels).size !== labels.length) {
        showMsg("Não use dois campos personalizados com o mesmo nome.", "error");
        return;
      }

      if (activeFields.some((field: any) => field.fieldType === "select" && (!Array.isArray(field.options) || field.options.filter((option: string) => option.trim()).length < 2))) {
        showMsg("Campos do tipo Lista/Seleção precisam ter pelo menos duas opções.", "error");
        return;
      }
    }

    setSaving(true);

    window.setTimeout(() => {
      if (editType === "banner") {
        const list = [...data.banners];
        const idx = list.findIndex(i => i.id === editing.id);
        if (idx >= 0) list[idx] = editing; else list.push(editing);
        setBanners(list);
      } else if (editType === "brand") {
        const list = [...data.brands];
        const idx = list.findIndex(i => i.id === editing.id);
        if (idx >= 0) list[idx] = editing; else list.push(editing);
        setBrands(list);
      } else if (editType === "category") {
        const list = [...data.categories];
        const idx = list.findIndex(i => i.id === editing.id);
        if (idx >= 0) list[idx] = editing; else list.push(editing);
        setCategories(list);
      } else if (editType === "product") {
        const base = editOrigin === "promotions" ? [...data.products] : [...productsDraft];
        const idx = base.findIndex(i => i.id === editing.id);
        if (idx >= 0) base[idx] = editing; else base.push(editing);

        if (editOrigin === "promotions") {
          setProducts(base);
          setProductsDraft(base);
          setProductsDirty(false);
          setPromotionsDraft(prev => prev.includes(editing.id) ? prev : [...prev, editing.id]);
          setPromotionsDirty(true);
          showMsg("Produto cadastrado e marcado. Clique em Salvar Alterações nas Promoções.");
        } else {
          setProductsDraft(base);
          setProductsDirty(true);
          showMsg("Produto alterado. Clique em Salvar Alterações para gravar.");
        }
      } else if (editType === "seller" || editType === "sellerAvailability") {
        const list = [...data.sellers];
        const idx = list.findIndex(i => i.id === editing.id);
        if (idx >= 0) list[idx] = editing; else list.push(editing);
        setSellers(list);
      } else if (editType === "newsItem") {
        const list = [...data.news];
        const idx = list.findIndex(i => i.id === editing.id);
        if (idx >= 0) list[idx] = editing; else list.push(editing);
        setNews(list);
      } else if (editType === "rssSource") {
        const list = [...data.rssSources];
        const idx = list.findIndex(i => i.id === editing.id);
        if (idx >= 0) list[idx] = editing; else list.push(editing);
        setRssSources(list);
      } else if (editType === "store") {
        const list = [...data.stores];
        const idx = list.findIndex(i => i.id === editing.id);
        if (idx >= 0) list[idx] = editing; else list.push(editing);
        setStores(list);
      } else if (editType === "company" || editType === "companyConstruSete") {
        setCompanyInfo(editing);
      } else if (editType === "companyTimeline") {
        const list = [...data.companyTimeline];
        const idx = list.findIndex(i => i.id === editing.id);

        if (idx >= 0) {
          list[idx] = editing;
        } else {
          list.push(editing);
        }

        const ordered = list
          .sort((a, b) => a.eventYear - b.eventYear || a.id - b.id)
          .map((item, index) => ({
            ...item,
            sortOrder: index,
          }));

        setCompanyTimeline(ordered);
      } else if (editType === "testimonial") {
        const list = [...data.testimonials];
        const idx = list.findIndex(i => i.id === editing.id);
        if (idx >= 0) list[idx] = editing; else list.push(editing);
        setTestimonials(list);
      } else if (editType === "jobVacancy") {
        const list = [...data.jobVacancies];
        const idx = list.findIndex(i => i.id === editing.id);
        if (idx >= 0) list[idx] = editing; else list.push(editing);
        setJobVacancies(list);
      } else if (editType === "raffle") {
        const list = [...data.raffles];
        const idx = list.findIndex(i => i.id === editing.id);
        if (idx >= 0) list[idx] = editing; else list.push(editing);

        setRaffles(
          editing.active
            ? list.map(item => item.id === editing.id ? editing : { ...item, active: false })
            : list
        );
      }

      closeEdit();
      setSaving(false);

      if (editType !== "product") {
        showMsg("Salvo com sucesso!");
      }
    }, 200);
  };

  const performDelete = (type: string, id: number) => {
    if (type === "banner") setBanners(data.banners.filter(i => i.id !== id));
    else if (type === "brand") setBrands(data.brands.filter(i => i.id !== id));
    else if (type === "category") setCategories(data.categories.filter(i => i.id !== id));
    else if (type === "seller") setSellers(data.sellers.filter(i => i.id !== id));
    else if (type === "newsItem") setNews(data.news.filter(i => i.id !== id));
    else if (type === "rssSource") setRssSources(data.rssSources.filter(i => i.id !== id));
    else if (type === "store") setStores(data.stores.filter(i => i.id !== id));
    else if (type === "companyTimeline") {
      const ordered = data.companyTimeline
        .filter(i => i.id !== id)
        .sort((a, b) => a.eventYear - b.eventYear || a.id - b.id)
        .map((item, index) => ({
          ...item,
          sortOrder: index,
        }));

      setCompanyTimeline(ordered);
    }
    else if (type === "testimonial") setTestimonials(data.testimonials.filter(i => i.id !== id));
    else if (type === "jobVacancy") setJobVacancies(data.jobVacancies.filter(i => i.id !== id));
    else if (type === "raffle") setRaffles(data.raffles.filter(i => i.id !== id));
    showMsg("Item excluído!");
  };

  const handleDelete = (type: string, id: number) => {
    requestConfirm(
      "Excluir item",
      "Tem certeza que deseja excluir este item?",
      () => performDelete(type, id),
      "Excluir",
      true,
    );
  };

  const handleReset = () => {
    requestConfirm(
      "Recarregar dados",
      "Recarregar os dados atuais do banco? Alterações ainda não salvas nesta tela serão descartadas.",
      () => {
        setSettingsDirty(false);
        setProductsDirty(false);
        setPromotionsDirty(false);
        setOutletDirty(false);
        resetAll();
        showMsg("Dados recarregados do banco.");
      },
      "Recarregar",
      true,
    );
  };

  const updateField = (field: string, value: any) => {
    setEditing((prev: any) => ({ ...prev, [field]: value }));
  };

  const updateNestedField = (field: string, idx: number, value: any) => {
    setEditing((prev: any) => {
      const arr = [...(prev[field] || [])];
      arr[idx] = value;
      return { ...prev, [field]: arr };
    });
  };

  const addNestedField = (field: string) => {
    setEditing((prev: any) => ({
      ...prev,
      [field]: [
        ...(prev[field] || []),
        field === "subcategories"
          ? { id: newId(prev[field] || []), name: "" }
          : "",
      ],
    }));
  };

  const removeNestedField = (field: string, idx: number) => {
    setEditing((prev: any) => {
      const arr = [...(prev[field] || [])];
      arr.splice(idx, 1);
      return { ...prev, [field]: arr };
    });
  };

  const addRafflePrize = () => {
    setEditing((prev: Raffle) => ({
      ...prev,
      prizes: [...prev.prizes, { id: newId(prev.prizes), title: "", description: "", image: "" }],
    }));
  };

  const addRaffleField = () => {
    setEditing((prev: Raffle) => {
      const usedIds = [
        ...data.raffles.flatMap(raffle => raffle.fields || []).map(field => field.id),
        ...(prev.fields || []).map(field => field.id),
      ];
      const nextId = usedIds.length > 0 ? Math.max(...usedIds) + 1 : 1;

      const field: RaffleField = {
        id: nextId,
        label: "",
        fieldType: "text",
        required: false,
        options: [],
        showOnTicket: true,
        active: true,
      };

      return {
        ...prev,
        fields: [...(prev.fields || []), field],
      };
    });
  };

  const updateRaffleNested = (
    collection: "prizes" | "fields",
    idx: number,
    field: string,
    value: any,
  ) => {
    setEditing((prev: any) => {
      const items = [...(prev[collection] || [])];
      items[idx] = { ...items[idx], [field]: value };
      return { ...prev, [collection]: items };
    });
  };

  const removeRaffleNested = (collection: "prizes" | "fields", idx: number) => {
    setEditing((prev: any) => {
      const items = [...(prev[collection] || [])];
      items.splice(idx, 1);
      return { ...prev, [collection]: items };
    });
  };

  const removeRafflePrize = (idx: number) => {
    const prize = editing?.prizes?.[idx];

    if (prize && (editing?.winners || []).some((winner: any) => Number(winner.prizeId) === Number(prize.id))) {
      showMsg("Esse prêmio já possui ganhador. Desfaça o ganhador antes de remover o prêmio.", "error");
      return;
    }

    if ((editing?.prizes || []).length <= 1) {
      showMsg("O sorteio precisa ter pelo menos um prêmio.", "error");
      return;
    }

    removeRaffleNested("prizes", idx);
  };

  const removeRaffleField = (idx: number) => {
    requestConfirm(
      "Remover campo",
      "Remover este campo também excluirá as respostas já gravadas para ele quando o sorteio for salvo. Deseja continuar?",
      () => removeRaffleNested("fields", idx),
      "Remover",
      true,
    );
  };


  const handleSellerToggle = (seller: Seller) => {
    if (seller.available) {
      openEdit("sellerAvailability", seller);
      return;
    }

    setSellers(data.sellers.map(item => (
      item.id === seller.id
        ? { ...item, available: true, inactiveUntil: "" }
        : item
    )));
    showMsg("Vendedor reativado com sucesso!");
  };

  const toggleRssSource = (id: number) => {
    setRssSources(data.rssSources.map(s => s.id === id ? { ...s, enabled: !s.enabled } : s));
  };

  const filteredProducts = productsDraft.filter(product => {
    const query = productSearch.trim().toLowerCase();
    if (!query) return true;
    return [
      product.name,
      product.code,
      product.description,
      product.brand,
      product.slug,
      ...(product.specs || []),
    ].some(value => String(value || "").toLowerCase().includes(query));
  });

  const filteredPromotionProducts = data.products.filter(product => {
    const query = promotionSearch.trim().toLowerCase();
    if (!query) return true;
    return [
      product.name,
      product.code,
      product.description,
      product.brand,
      product.slug,
      ...(product.specs || []),
    ].some(value => String(value || "").toLowerCase().includes(query));
  });

  const renderProducts = () => (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-lg font-heading font-bold text-foreground-950">Gerenciar Produtos</h2>
          <p className="text-foreground-500 text-sm mt-1">Busque, edite, ative ou desative produtos e determine até quando ficarão visíveis.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => openEdit("product", undefined, "products")} className="flex items-center gap-1.5 bg-background-50 border border-background-300 hover:border-primary-400 text-foreground-700 px-3 py-2 rounded-md text-xs font-label font-semibold transition-all cursor-pointer whitespace-nowrap">
            <i className="ri-add-line" /> Adicionar Produto
          </button>
          <button onClick={handleSaveProducts} disabled={!productsDirty} className="flex items-center gap-1.5 bg-primary-500 hover:bg-primary-600 disabled:bg-background-300 disabled:text-foreground-500 text-background-50 px-4 py-2 rounded-md text-xs font-label font-semibold transition-all cursor-pointer disabled:cursor-not-allowed whitespace-nowrap">
            <i className="ri-save-line" /> {productsDirty ? "Salvar Alterações" : "Alterações Salvas"}
          </button>
        </div>
      </div>

      <div className="relative">
        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" />
        <input type="search" value={productSearch} onChange={e => setProductSearch(e.target.value)} placeholder="Buscar por nome, código, descrição, marca ou especificação..." className="w-full pl-9 pr-3 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" />
      </div>

      <p className="text-foreground-500 text-xs font-label">{filteredProducts.length} produto{filteredProducts.length !== 1 ? "s" : ""} encontrado{filteredProducts.length !== 1 ? "s" : ""}</p>

      <div className="space-y-2">
        {filteredProducts.map(product => (
          <div key={product.id} className="bg-background-50 border border-background-200 rounded-lg px-4 py-3">
            <div className="flex flex-col lg:flex-row lg:items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className={`font-label font-semibold text-foreground-950 text-sm ${!product.active ? "opacity-50" : ""}`}>{product.name}</p>
                <p className="text-foreground-500 text-xs">{product.brand}{product.code ? ` · Código: ${product.code}` : ""}{product.showPrice ? ` - R$${product.price.toFixed(2)}` : ""}{product.isPromo ? " 🔥" : ""}</p>
              </div>

              <div className="flex flex-wrap items-end gap-3">
                {product.active && (
                  <div>
                    <label className="block text-[11px] font-label font-semibold text-foreground-600 mb-1">Ativo até</label>
                    <input type="date" value={product.activeUntil || ""} min={todayString()} onChange={e => updateProductDraft(product.id, { activeUntil: e.target.value })} className="px-2 py-1.5 text-xs border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" />
                    <p className="text-[10px] text-foreground-400 mt-1">Em branco = sem prazo</p>
                  </div>
                )}

                <label className="relative inline-flex items-center cursor-pointer mb-4" title={product.active ? "Produto ativo" : "Produto inativo"}>
                  <input type="checkbox" checked={product.active !== false} onChange={e => updateProductDraft(product.id, { active: e.target.checked, activeUntil: e.target.checked ? product.activeUntil : "" })} className="sr-only peer" />
                  <div className="w-11 h-6 bg-background-300 rounded-full peer peer-checked:after:translate-x-5 peer-checked:bg-primary-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-background-50 after:border after:border-background-300 after:rounded-full after:h-5 after:w-5 after:transition-all" />
                </label>

                <button onClick={() => openEdit("product", product, "products")} className="w-8 h-8 flex items-center justify-center text-foreground-500 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-all cursor-pointer mb-3" title="Editar">
                  <i className="ri-edit-line text-sm" />
                </button>
                <button onClick={() => deleteProductDraft(product.id)} className="w-8 h-8 flex items-center justify-center text-foreground-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-all cursor-pointer mb-3" title="Excluir">
                  <i className="ri-delete-bin-line text-sm" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderPromotions = () => (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-lg font-heading font-bold text-foreground-950">Gerenciar Promoções</h2>
          <p className="text-foreground-500 text-sm mt-1">Ative os produtos em promoção e determine até que dia cada oferta ficará disponível.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => openEdit("product", undefined, "promotions")} className="flex items-center gap-1.5 bg-background-50 border border-background-300 hover:border-primary-400 text-foreground-700 px-3 py-2 rounded-md text-xs font-label font-semibold transition-all cursor-pointer whitespace-nowrap">
            <i className="ri-add-line" /> Criar Novo Produto
          </button>
          <button onClick={handleSavePromotions} disabled={!promotionsDirty} className="flex items-center gap-1.5 bg-primary-500 hover:bg-primary-600 disabled:bg-background-300 disabled:text-foreground-500 text-background-50 px-4 py-2 rounded-md text-xs font-label font-semibold transition-all cursor-pointer disabled:cursor-not-allowed whitespace-nowrap">
            <i className="ri-save-line" /> {promotionsDirty ? "Salvar Alterações" : "Alterações Salvas"}
          </button>
        </div>
      </div>

      <div className="relative">
        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" />
        <input type="search" value={promotionSearch} onChange={e => setPromotionSearch(e.target.value)} placeholder="Buscar por nome, código, descrição, marca ou especificação..." className="w-full pl-9 pr-3 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" />
      </div>

      <div className="space-y-2">
        {filteredPromotionProducts.map(product => {
          const enabled = promotionsDraft.includes(product.id);
          return (
            <div key={product.id} className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 bg-background-50 border border-background-200 rounded-lg px-4 py-3">
              <div className="flex-1 min-w-0">
                <p className={`font-label font-semibold text-foreground-950 text-sm ${!enabled ? "opacity-60" : ""}`}>{product.name}</p>
                <p className="text-foreground-500 text-xs">{product.brand}{product.code ? ` · Código: ${product.code}` : ""}</p>
              </div>

              <div className="flex flex-wrap items-end gap-3">
                {enabled && (
                  <div>
                    <label className="block text-[11px] font-label font-semibold text-foreground-600 mb-1">Promoção ativa até</label>
                    <input type="date" value={promotionDatesDraft[product.id] || ""} min={todayString()} onChange={e => updatePromotionDate(product.id, e.target.value)} className="px-2 py-1.5 text-xs border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" />
                    <p className="text-[10px] text-foreground-400 mt-1">Em branco = sem prazo</p>
                  </div>
                )}

                <label className="relative inline-flex items-center cursor-pointer mb-4" title={enabled ? "Promoção ativa" : "Promoção inativa"}>
                  <input type="checkbox" checked={enabled} onChange={() => togglePromotionDraft(product.id)} className="sr-only peer" />
                  <div className="w-11 h-6 bg-background-300 rounded-full peer peer-checked:after:translate-x-5 peer-checked:bg-primary-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-background-50 after:border after:border-background-300 after:rounded-full after:h-5 after:w-5 after:transition-all" />
                </label>

                <button
                  onClick={() => openEdit("product", product, "promotions")}
                  className="w-8 h-8 flex items-center justify-center text-foreground-500 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-all cursor-pointer mb-3"
                  title="Editar produto"
                >
                  <i className="ri-edit-line text-sm" />
                </button>

                <button
                  onClick={() => deletePromotionProduct(product.id)}
                  className="w-8 h-8 flex items-center justify-center text-foreground-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-all cursor-pointer mb-3"
                  title="Excluir produto"
                >
                  <i className="ri-delete-bin-line text-sm" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderSellers = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-heading font-bold text-foreground-950">Gerenciar Vendedores</h2>
          <p className="text-foreground-500 text-sm mt-1">Desative temporariamente vendedores em férias ou afastamento e informe a data de retorno.</p>
        </div>
        <button onClick={() => openEdit("seller")} className="flex items-center gap-1.5 bg-primary-500 hover:bg-primary-600 text-background-50 px-3 py-2 rounded-md text-xs font-label font-semibold transition-all cursor-pointer whitespace-nowrap">
          <i className="ri-add-line" /> Adicionar
        </button>
      </div>

      <div className="space-y-2">
        {data.sellers.map(seller => (
          <div key={seller.id} className="flex items-center justify-between gap-3 bg-background-50 border border-background-200 rounded-lg px-4 py-3">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {seller.photo ? <img src={seller.photo} alt={seller.name} className="w-10 h-10 rounded-full object-cover" /> : <div className="w-10 h-10 rounded-full bg-background-200 flex items-center justify-center text-foreground-400"><i className="ri-user-line" /></div>}
              <div className="min-w-0">
                <p className={`font-label font-semibold text-foreground-950 text-sm ${!seller.available ? "opacity-50" : ""}`}>{seller.name}</p>
                <p className="text-foreground-500 text-xs">{seller.role} · {seller.store === "central" ? "Loja Central" : "MZ Outlet"}</p>
                {!seller.available && <p className="text-red-600 text-xs mt-0.5">Afastado até {seller.inactiveUntil ? new Date(`${seller.inactiveUntil}T12:00:00`).toLocaleDateString("pt-BR") : "data não informada"}</p>}
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <label className="relative inline-flex items-center cursor-pointer" title={seller.available ? "Vendedor ativo" : "Vendedor afastado"}>
                <input type="checkbox" checked={seller.available} onChange={() => handleSellerToggle(seller)} className="sr-only peer" />
                <div className="w-11 h-6 bg-background-300 rounded-full peer peer-checked:after:translate-x-5 peer-checked:bg-primary-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-background-50 after:border after:border-background-300 after:rounded-full after:h-5 after:w-5 after:transition-all" />
              </label>
              <button onClick={() => openEdit("seller", seller)} className="w-8 h-8 flex items-center justify-center text-foreground-500 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-all cursor-pointer" title="Editar"><i className="ri-edit-line text-sm" /></button>
              <button onClick={() => handleDelete("seller", seller.id)} className="w-8 h-8 flex items-center justify-center text-foreground-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-all cursor-pointer" title="Excluir"><i className="ri-delete-bin-line text-sm" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderReceivedForms = () => {
    const isContact = formsTab === "contact";
    const total = isContact ? contactMessagesTotal : jobApplicationsTotal;

    return (
      <div className="space-y-5">
        <div>
          <h2 className="text-lg font-heading font-bold text-foreground-950">Formulários Recebidos</h2>
          <p className="text-foreground-500 text-sm mt-1">Consulte, pesquise e exclua as mensagens do Contato e as candidaturas recebidas pelo Trabalhe Conosco.</p>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="inline-flex bg-background-200 rounded-lg p-1 self-start">
            <button
              type="button"
              onClick={() => { setFormsTab("contact"); setFormSearch(""); }}
              className={`px-4 py-2 rounded-md text-sm font-label font-semibold transition-all cursor-pointer ${formsTab === "contact" ? "bg-background-50 text-primary-600 shadow-sm" : "text-foreground-600 hover:text-foreground-950"}`}
            >
              <i className="ri-mail-line mr-1.5" /> Contato
            </button>
            <button
              type="button"
              onClick={() => { setFormsTab("rh"); setFormSearch(""); }}
              className={`px-4 py-2 rounded-md text-sm font-label font-semibold transition-all cursor-pointer ${formsTab === "rh" ? "bg-background-50 text-primary-600 shadow-sm" : "text-foreground-600 hover:text-foreground-950"}`}
            >
              <i className="ri-briefcase-line mr-1.5" /> RH / Currículos
            </button>
          </div>

          <div className="text-xs font-label text-foreground-500">
            {total} {total === 1 ? "registro" : "registros"}
          </div>
        </div>

        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" />
          <input
            type="search"
            value={formSearch}
            onChange={e => setFormSearch(e.target.value)}
            placeholder={isContact ? "Pesquisar por nome, e-mail, telefone, assunto ou mensagem..." : "Pesquisar por nome, e-mail, telefone, vaga, experiência ou resumo..."}
            className="w-full pl-10 pr-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400"
          />
        </div>

        {formsLoading ? (
          <div className="bg-background-50 border border-background-200 rounded-lg p-8 text-center">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-500 block mb-2" />
            <p className="text-foreground-500 text-sm font-label">Carregando registros...</p>
          </div>
        ) : isContact ? (
          <div className="space-y-3">
            {contactMessages.map(entry => (
              <div key={entry.id} className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <h3 className="font-heading font-bold text-foreground-950 text-base">{entry.name}</h3>
                      <span className="bg-primary-50 text-primary-700 border border-primary-100 rounded-full px-2 py-0.5 text-[11px] font-label font-semibold">{entry.subject}</span>
                    </div>

                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs font-label text-foreground-500 mb-3">
                      <a href={`mailto:${entry.email}`} className="hover:text-primary-600"><i className="ri-mail-line mr-1" />{entry.email}</a>
                      {entry.phone && <a href={`tel:${entry.phone.replace(/\D/g, "")}`} className="hover:text-primary-600"><i className="ri-phone-line mr-1" />{entry.phone}</a>}
                      <span><i className="ri-time-line mr-1" />{formatReceivedDate(entry.createdAt)}</span>
                    </div>

                    <div className="bg-background-100 rounded-md p-3">
                      <p className="text-foreground-700 text-sm whitespace-pre-wrap leading-relaxed">{entry.message}</p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => requestConfirm(
                      "Excluir mensagem",
                      `Deseja excluir definitivamente a mensagem de ${entry.name}?`,
                      () => deleteReceivedForm("contact", entry.id),
                      "Excluir",
                      true,
                    )}
                    className="self-end md:self-start w-9 h-9 flex items-center justify-center text-foreground-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-all cursor-pointer shrink-0"
                    title="Excluir mensagem"
                  >
                    <i className="ri-delete-bin-line" />
                  </button>
                </div>
              </div>
            ))}

            {contactMessages.length === 0 && (
              <div className="bg-background-50 border border-dashed border-background-300 rounded-lg p-8 text-center">
                <i className="ri-mail-open-line text-3xl text-foreground-300 mb-3 block" />
                <p className="text-foreground-500 text-sm font-label">Nenhuma mensagem encontrada.</p>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {jobApplications.map(entry => (
              <div key={entry.id} className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <h3 className="font-heading font-bold text-foreground-950 text-base">{entry.name}</h3>
                      <span className="bg-primary-50 text-primary-700 border border-primary-100 rounded-full px-2 py-0.5 text-[11px] font-label font-semibold">{entry.position}</span>
                    </div>

                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs font-label text-foreground-500 mb-3">
                      <a href={`mailto:${entry.email}`} className="hover:text-primary-600"><i className="ri-mail-line mr-1" />{entry.email}</a>
                      <a href={`tel:${entry.phone.replace(/\D/g, "")}`} className="hover:text-primary-600"><i className="ri-phone-line mr-1" />{entry.phone}</a>
                      <span><i className="ri-time-line mr-1" />{formatReceivedDate(entry.createdAt)}</span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                      <div className="bg-background-100 rounded-md p-3">
                        <p className="text-[11px] uppercase tracking-wide text-foreground-400 font-label mb-1">Experiência</p>
                        <p className="text-foreground-800 text-sm font-label font-semibold">{entry.experience}</p>
                      </div>
                      <div className="bg-background-100 rounded-md p-3">
                        <p className="text-[11px] uppercase tracking-wide text-foreground-400 font-label mb-1">Vaga</p>
                        <p className="text-foreground-800 text-sm font-label font-semibold">{entry.position}</p>
                      </div>
                    </div>

                    <div className="bg-background-100 rounded-md p-3">
                      <p className="text-[11px] uppercase tracking-wide text-foreground-400 font-label mb-1">Resumo profissional</p>
                      <p className="text-foreground-700 text-sm whitespace-pre-wrap leading-relaxed">{entry.resumeSummary || "Não informado"}</p>
                    </div>

                    {entry.resumeFile && (
                      <a href={entry.resumeFile} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-3 text-xs font-label font-semibold text-primary-600 hover:text-primary-700">
                        <i className="ri-file-text-line" /> Abrir currículo anexado
                      </a>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => requestConfirm(
                      "Excluir candidatura",
                      `Deseja excluir definitivamente a candidatura de ${entry.name}?`,
                      () => deleteReceivedForm("rh", entry.id),
                      "Excluir",
                      true,
                    )}
                    className="self-end md:self-start w-9 h-9 flex items-center justify-center text-foreground-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-all cursor-pointer shrink-0"
                    title="Excluir candidatura"
                  >
                    <i className="ri-delete-bin-line" />
                  </button>
                </div>
              </div>
            ))}

            {jobApplications.length === 0 && (
              <div className="bg-background-50 border border-dashed border-background-300 rounded-lg p-8 text-center">
                <i className="ri-briefcase-line text-3xl text-foreground-300 mb-3 block" />
                <p className="text-foreground-500 text-sm font-label">Nenhuma candidatura encontrada.</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderVacancies = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-heading font-bold text-foreground-950">Vagas do Trabalhe Conosco</h2>
          <p className="text-foreground-500 text-sm mt-1">Cadastre e controle as vagas que serão mostradas na página Trabalhe Conosco.</p>
        </div>
        <button onClick={() => openEdit("jobVacancy")} className="flex items-center gap-1.5 bg-primary-500 hover:bg-primary-600 text-background-50 px-3 py-2 rounded-md text-xs font-label font-semibold transition-all cursor-pointer whitespace-nowrap"><i className="ri-add-line" /> Adicionar Vaga</button>
      </div>

      {renderList(data.jobVacancies, "jobVacancy", (vacancy: JobVacancy) => (
        <div>
          <div className="flex items-center gap-2">
            <p className={`font-label font-semibold text-foreground-950 text-sm ${!vacancy.active ? "opacity-50" : ""}`}>{vacancy.title}</p>
            {vacancy.urgent && <span className="bg-primary-100 text-primary-700 text-[10px] font-label font-bold px-2 py-0.5 rounded-full">Urgente</span>}
          </div>
          <p className="text-foreground-500 text-xs">{vacancy.type}{!vacancy.active ? " · Inativa" : ""}</p>
        </div>
      ), false)}
    </div>
  );

  const renderRaffles = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-heading font-bold text-foreground-950">Gerenciar Sorteios</h2>
          <p className="text-foreground-500 text-sm mt-1">Crie o sorteio, os itens que serão sorteados, os campos de cadastro e gerencie os concorrentes.</p>
        </div>
        <button onClick={() => openEdit("raffle")} className="flex items-center gap-1.5 bg-primary-500 hover:bg-primary-600 text-background-50 px-3 py-2 rounded-md text-xs font-label font-semibold transition-all cursor-pointer whitespace-nowrap"><i className="ri-add-line" /> Novo Sorteio</button>
      </div>

      <div className="bg-primary-50 border border-primary-100 rounded-lg px-4 py-3">
        <p className="text-primary-800 text-xs font-label">Somente um sorteio fica ativo como vigente. Enquanto estiver ativo e dentro das datas, o banner aparece na página inicial e o cadastro fica disponível. Sorteios encerrados podem continuar no histórico.</p>
      </div>

      <div className="space-y-2">
        {data.raffles.map((raffle: Raffle) => (
          <div key={raffle.id} className="bg-background-50 border border-background-200 rounded-lg px-4 py-3">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                {raffle.bannerImage ? (isVideoMedia(raffle.bannerImage) ? <video src={raffle.bannerImage} className="w-14 h-10 rounded object-cover" muted playsInline preload="metadata" /> : <img src={raffle.bannerImage} alt={raffle.title} className="w-14 h-10 rounded object-cover" />) : <div className="w-14 h-10 rounded bg-background-200 flex items-center justify-center text-foreground-400"><i className="ri-gift-line" /></div>}
                <div className="min-w-0">
                  <p className={`font-label font-semibold text-foreground-950 text-sm ${!raffle.active ? "opacity-50" : ""}`}>{raffle.title}</p>
                  <p className="text-foreground-500 text-xs">{raffle.active ? "Ativo" : "Inativo"}{raffle.endDate ? ` · até ${new Date(`${raffle.endDate}T12:00:00`).toLocaleDateString("pt-BR")}` : ""} · {raffle.prizes.length} prêmio{raffle.prizes.length !== 1 ? "s" : ""} · {(raffle.fields || []).filter(field => field.active).length} campo{(raffle.fields || []).filter(field => field.active).length !== 1 ? "s" : ""} extra{(raffle.fields || []).filter(field => field.active).length !== 1 ? "s" : ""} · {raffle.winners.length} ganhador{raffle.winners.length !== 1 ? "es" : ""}</p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 shrink-0">
                <button onClick={() => openRaffleManager(raffle.id)} className="flex items-center gap-1.5 bg-accent-500 hover:bg-accent-600 text-background-50 px-3 py-2 rounded-md text-xs font-label font-semibold transition-all cursor-pointer whitespace-nowrap"><i className="ri-team-line" /> Concorrentes / Ganhadores</button>
                <button onClick={() => openEdit("raffle", raffle)} className="w-8 h-8 flex items-center justify-center text-foreground-500 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-all cursor-pointer" title="Editar sorteio"><i className="ri-edit-line text-sm" /></button>
                <button onClick={() => handleDelete("raffle", raffle.id)} className="w-8 h-8 flex items-center justify-center text-foreground-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-all cursor-pointer" title="Excluir sorteio"><i className="ri-delete-bin-line text-sm" /></button>
              </div>
            </div>
          </div>
        ))}

        {data.raffles.length === 0 && <div className="bg-background-50 border border-dashed border-background-300 rounded-lg p-8 text-center text-foreground-500 text-sm font-label">Nenhum sorteio cadastrado.</div>}
      </div>
    </div>
  );

  const renderNews = () => (
    <div className="space-y-8">
      <div>
        <h2 className="text-lg font-heading font-bold text-foreground-950">Fontes de Notícias (RSS)</h2>
        <p className="text-foreground-500 text-sm mt-1 mb-4">O site busca automaticamente as notícias mais recentes desses sites confiáveis. Você pode editar, desativar ou adicionar novas fontes. As notícias se renovam sozinhas conforme novas são publicadas.</p>
        <div className="bg-accent-100 border border-accent-200 rounded-lg px-4 py-3 mb-4 flex items-center gap-3">
          <i className="ri-rss-line text-accent-700 text-lg" />
          <p className="text-accent-900 text-xs font-label">As notícias são exibidas ao vivo no site. Máximo de 30 notícias por página — as mais antigas saem automaticamente quando novas chegam.</p>
        </div>
        <div className="space-y-2">
          {data.rssSources.map(source => (
            <div key={source.id} className="flex items-center justify-between bg-background-50 border border-background-200 rounded-lg px-4 py-3">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className={`w-9 h-9 flex items-center justify-center rounded-full ${source.enabled ? "bg-accent-100 text-accent-700" : "bg-background-200 text-foreground-400"}`}><i className="ri-rss-fill text-sm" /></div>
                <div className="min-w-0">
                  <p className={`font-label font-semibold text-foreground-950 text-sm ${!source.enabled ? "opacity-50" : ""}`}>{source.name}</p>
                  <p className="text-foreground-500 text-xs truncate">{source.url}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 ml-3 shrink-0">
                <label className="relative inline-flex items-center cursor-pointer" title={source.enabled ? "Fonte ativa" : "Fonte inativa"}>
                  <input type="checkbox" checked={source.enabled} onChange={() => toggleRssSource(source.id)} className="sr-only peer" />
                  <div className="w-9 h-5 bg-background-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-background-50 after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-background-50 after:border-background-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-accent-500" />
                </label>
                <button onClick={() => openEdit("rssSource", source)} className="w-8 h-8 flex items-center justify-center text-foreground-500 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-all cursor-pointer" title="Editar"><i className="ri-edit-line text-sm" /></button>
                <button onClick={() => handleDelete("rssSource", source.id)} className="w-8 h-8 flex items-center justify-center text-foreground-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-all cursor-pointer" title="Excluir"><i className="ri-delete-bin-line text-sm" /></button>
              </div>
            </div>
          ))}
          {data.rssSources.length === 0 && (
            <div className="bg-background-50 border border-dashed border-background-300 rounded-lg p-8 text-center">
              <i className="ri-rss-line text-3xl text-foreground-300 mb-3 block" />
              <p className="text-foreground-500 text-sm font-label">Nenhuma fonte RSS cadastrada.</p>
              <p className="text-foreground-400 text-xs mt-1">Clique em "Adicionar Fonte" para incluir a primeira.</p>
            </div>
          )}
        </div>
        <div className="mt-4">
          <button onClick={() => openEdit("rssSource")} className="flex items-center gap-1.5 bg-primary-500 hover:bg-primary-600 text-background-50 px-4 py-2 rounded-md text-xs font-label font-semibold transition-all cursor-pointer whitespace-nowrap"><i className="ri-add-line" /> Adicionar Fonte</button>
        </div>
      </div>

      <div className="border-t border-background-200 pt-6">
        <h3 className="text-base font-heading font-bold text-foreground-950 mb-1">Notícias Fixas (reserva)</h3>
        <p className="text-foreground-500 text-xs mb-4">Estas notícias aparecem apenas se nenhuma fonte RSS estiver funcionando no momento, como reserva de segurança.</p>
        {renderList(data.news, "newsItem", (n: NewsItem) => <div><p className="font-label font-semibold text-foreground-950 text-sm">{n.title}</p><p className="text-foreground-500 text-xs">{n.category} • {n.date}</p></div>)}
      </div>
    </div>
  );

  const formatBackupDate = (ts: number) => {
    const d = new Date(ts);
    return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const renderOutletMediaField = (
    field: "heroMedia" | "showcaseMediaOne" | "showcaseMediaTwo",
    label: string,
    key: string,
  ) => {
    const value = outletDraft[field] || "";

    return (
      <div className="bg-background-50 border border-background-200 rounded-lg p-4">
        <label className="block text-xs font-label font-semibold text-foreground-700 mb-1">{label}</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={value}
            onChange={e => updateOutletDraft(field, e.target.value)}
            placeholder="Cole um link ou escolha uma mídia"
            className="flex-1 min-w-0 px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400"
          />
          {/^https?:\/\//i.test(value) && (
            <button
              type="button"
              disabled={uploadingKey === `${key}-url`}
              onClick={async () => {
                const path = await importImageFromUrl(value, "outlet", `${key}-url`, true);
                if (path) updateOutletDraft(field, path);
              }}
              className="px-3 py-2 border border-background-300 hover:border-primary-400 rounded-md text-xs font-label font-semibold text-foreground-700 cursor-pointer disabled:opacity-50 whitespace-nowrap"
            >
              Importar link
            </button>
          )}
        </div>
        <label className="mt-2 inline-flex items-center gap-2 border border-background-300 hover:border-primary-400 px-3 py-2 rounded-md text-xs font-label font-semibold text-foreground-700 cursor-pointer">
          <i className={uploadingKey === key ? "ri-loader-4-line animate-spin" : "ri-upload-2-line"} />
          Escolher mídia
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm"
            className="hidden"
            disabled={uploadingKey === key}
            onChange={async e => {
              const file = e.target.files?.[0];
              if (!file) return;
              const path = await uploadImage(file, "outlet", key, true);
              if (path) updateOutletDraft(field, path);
            }}
          />
        </label>
        <p className="text-foreground-400 text-[11px] mt-1">JPG, PNG, WebP e GIF; vídeos MP4 ou WebM. Imagens estáticas até 10 MB; GIFs e vídeos até 50 MB.</p>
        {value && (
          isVideoMedia(value) ? (
            <video src={value} className="mt-3 w-full max-h-48 rounded-md object-cover bg-background-100" autoPlay muted loop playsInline preload="metadata" />
          ) : (
            <img src={value} alt={label} className="mt-3 w-full max-h-48 rounded-md object-cover bg-background-100" />
          )
        )}
      </div>
    );
  };

  const renderOutlet = () => {
    const outletStore = data.stores.find(store => /outlet/i.test(store.name)) || data.stores[1];
    const outletSellers = data.sellers.filter(seller => seller.store === "filial");
    const selectedProducts = outletProductsDraft
      .map(id => data.products.find(product => product.id === id))
      .filter((product): product is Product => Boolean(product));
    const query = outletProductSearch.trim().toLowerCase();
    const availableProducts = data.products.filter(product => {
      if (outletProductsDraft.includes(product.id)) return false;
      if (!query) return true;
      return [product.name, product.brand, product.slug, product.description]
        .some(value => String(value || "").toLowerCase().includes(query));
    });

    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-heading font-bold text-foreground-950">Página MZ Outlet</h2>
            <p className="text-foreground-500 text-sm mt-1">Edite os textos, mídias e produtos exibidos na página exclusiva da MZ Outlet.</p>
          </div>
          <button
            type="button"
            onClick={handleSaveOutlet}
            disabled={!outletDirty}
            className="flex items-center gap-2 bg-primary-500 hover:bg-primary-600 disabled:bg-background-300 disabled:text-foreground-500 text-background-50 px-4 py-2 rounded-md text-sm font-label font-semibold transition-all cursor-pointer disabled:cursor-not-allowed whitespace-nowrap"
          >
            <i className="ri-save-line" />
            {outletDirty ? "Salvar MZ Outlet" : "Alterações Salvas"}
          </button>
        </div>

        <div className="bg-background-50 border border-background-200 rounded-lg p-5">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div>
              <h3 className="font-heading font-bold text-foreground-950">Publicação</h3>
              <p className="text-foreground-500 text-xs mt-1">Desative para ocultar temporariamente o conteúdo da página.</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" checked={outletDraft.active} onChange={e => updateOutletDraft("active", e.target.checked)} className="sr-only peer" />
              <div className="w-11 h-6 bg-background-300 rounded-full peer peer-checked:bg-primary-500 after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-background-50 after:border after:border-background-300 after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full" />
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Título principal</label><input type="text" value={outletDraft.title} onChange={e => updateOutletDraft("title", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div>
            <div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Subtítulo</label><input type="text" value={outletDraft.subtitle} onChange={e => updateOutletDraft("subtitle", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div>
          </div>
          <div className="mt-4"><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Descrição do cabeçalho</label><textarea rows={3} value={outletDraft.description} onChange={e => updateOutletDraft("description", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400 resize-y" /></div>
          <div className="mt-4">{renderOutletMediaField("heroMedia", "Mídia do cabeçalho", "outlet-hero")}</div>
        </div>

        <div className="bg-background-50 border border-background-200 rounded-lg p-5 space-y-4">
          <h3 className="font-heading font-bold text-foreground-950">Apresentação</h3>
          <div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Título</label><input type="text" value={outletDraft.introTitle} onChange={e => updateOutletDraft("introTitle", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div>
          <div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Texto</label><textarea rows={4} value={outletDraft.introText} onChange={e => updateOutletDraft("introText", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400 resize-y" /></div>
        </div>

        <div className="bg-background-50 border border-background-200 rounded-lg p-5 space-y-4">
          <h3 className="font-heading font-bold text-foreground-950">Destaques e mídias</h3>
          <div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Título</label><input type="text" value={outletDraft.showcaseTitle} onChange={e => updateOutletDraft("showcaseTitle", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div>
          <div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Texto</label><textarea rows={3} value={outletDraft.showcaseText} onChange={e => updateOutletDraft("showcaseText", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400 resize-y" /></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderOutletMediaField("showcaseMediaOne", "Mídia 1", "outlet-media-1")}
            {renderOutletMediaField("showcaseMediaTwo", "Mídia 2", "outlet-media-2")}
          </div>
        </div>

        <div className="bg-background-50 border border-background-200 rounded-lg p-5 space-y-4">
          <h3 className="font-heading font-bold text-foreground-950">Seção de produtos</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Título</label><input type="text" value={outletDraft.productsTitle} onChange={e => updateOutletDraft("productsTitle", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div>
            <div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Texto</label><input type="text" value={outletDraft.productsText} onChange={e => updateOutletDraft("productsText", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div>
          </div>

          <div>
            <p className="text-xs font-label font-semibold text-foreground-700 mb-2">Produtos selecionados ({selectedProducts.length})</p>
            <div className="space-y-2">
              {selectedProducts.map((product, index) => (
                <div key={product.id} className="flex items-center justify-between gap-3 border border-background-200 rounded-md p-3">
                  <div className="flex items-center gap-3 min-w-0">
                    {product.images[0] ? <img src={product.images[0]} alt={product.name} className="w-11 h-11 rounded-md object-cover bg-background-100 shrink-0" /> : <div className="w-11 h-11 rounded-md bg-background-100 flex items-center justify-center text-foreground-400 shrink-0"><i className="ri-image-line" /></div>}
                    <div className="min-w-0"><p className="font-label font-semibold text-foreground-950 text-sm truncate">{product.name}</p><p className="text-foreground-500 text-xs truncate">{product.brand}</p></div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button type="button" disabled={index === 0} onClick={() => moveOutletProduct(product.id, -1)} className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:text-primary-600 hover:bg-primary-50 disabled:opacity-30 cursor-pointer" title="Subir"><i className="ri-arrow-up-line" /></button>
                    <button type="button" disabled={index === selectedProducts.length - 1} onClick={() => moveOutletProduct(product.id, 1)} className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:text-primary-600 hover:bg-primary-50 disabled:opacity-30 cursor-pointer" title="Descer"><i className="ri-arrow-down-line" /></button>
                    <button type="button" onClick={() => removeOutletProduct(product.id)} className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:text-red-600 hover:bg-red-50 cursor-pointer" title="Remover"><i className="ri-close-line" /></button>
                  </div>
                </div>
              ))}
              {selectedProducts.length === 0 && <div className="border border-dashed border-background-300 rounded-md p-5 text-center text-foreground-500 text-sm">Nenhum produto selecionado para a Outlet.</div>}
            </div>
          </div>

          <div className="border-t border-background-200 pt-4">
            <label className="block text-xs font-label font-semibold text-foreground-700 mb-2">Adicionar produtos existentes</label>
            <div className="relative mb-3"><i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" /><input type="text" value={outletProductSearch} onChange={e => setOutletProductSearch(e.target.value)} placeholder="Pesquisar por nome, marca ou descrição..." className="w-full pl-9 pr-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div>
            <div className="max-h-72 overflow-y-auto space-y-2 pr-1">
              {availableProducts.slice(0, 100).map(product => (
                <div key={product.id} className="flex items-center justify-between gap-3 border border-background-200 rounded-md px-3 py-2">
                  <div className="min-w-0"><p className="font-label font-semibold text-foreground-950 text-sm truncate">{product.name}</p><p className="text-foreground-500 text-xs truncate">{product.brand}</p></div>
                  <button type="button" onClick={() => addOutletProduct(product.id)} className="flex items-center gap-1 bg-primary-50 hover:bg-primary-100 text-primary-700 px-3 py-1.5 rounded-md text-xs font-label font-semibold cursor-pointer whitespace-nowrap"><i className="ri-add-line" /> Adicionar</button>
                </div>
              ))}
              {availableProducts.length === 0 && <p className="text-foreground-500 text-sm text-center py-4">Nenhum produto disponível para adicionar.</p>}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-background-50 border border-background-200 rounded-lg p-5 space-y-3">
            <h3 className="font-heading font-bold text-foreground-950">Seção de vendedores</h3>
            <div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Título</label><input type="text" value={outletDraft.sellersTitle} onChange={e => updateOutletDraft("sellersTitle", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div>
            <div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Texto</label><textarea rows={3} value={outletDraft.sellersText} onChange={e => updateOutletDraft("sellersText", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400 resize-y" /></div>
            <p className="text-foreground-500 text-xs">Vendedores vinculados à MZ Outlet: <strong>{outletSellers.length}</strong>. O cadastro continua sendo feito na seção Vendedores.</p>
            <button type="button" onClick={() => setSection("sellers")} className="text-primary-600 hover:text-primary-700 text-xs font-label font-semibold cursor-pointer">Gerenciar vendedores →</button>
          </div>

          <div className="bg-background-50 border border-background-200 rounded-lg p-5 space-y-3">
            <h3 className="font-heading font-bold text-foreground-950">Seção de contato e endereço</h3>
            <div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Título</label><input type="text" value={outletDraft.contactTitle} onChange={e => updateOutletDraft("contactTitle", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div>
            <div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Texto</label><textarea rows={3} value={outletDraft.contactText} onChange={e => updateOutletDraft("contactText", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400 resize-y" /></div>
            {outletStore ? <div className="text-foreground-500 text-xs leading-relaxed"><p className="font-semibold text-foreground-700">{outletStore.name}</p><p>{outletStore.address}</p><p>{outletStore.city}</p><p>{outletStore.phone}</p></div> : <p className="text-red-600 text-xs">A loja MZ Outlet não foi localizada no cadastro de lojas.</p>}
            <p className="text-foreground-400 text-[11px]">Endereço, telefone, WhatsApp, horários e mapas são editados na seção Lojas para evitar dados duplicados.</p>
            <button type="button" onClick={() => setSection("stores")} className="text-primary-600 hover:text-primary-700 text-xs font-label font-semibold cursor-pointer">Gerenciar loja →</button>
          </div>
        </div>
      </div>
    );
  };

  const renderSettingsMediaField = (
    field: "allProductsBannerMedia" | "companyHeaderMedia" | "careersHeaderMedia",
    label: string,
    folder: string,
    key: string,
  ) => {
    const value = settingsDraft[field] || "";

    return (
      <div className="bg-background-50 border border-background-200 rounded-lg p-4">
        <label className="block text-xs font-label font-semibold text-foreground-700 mb-1">{label}</label>
        <div className="flex gap-2">
          <input type="text" value={value} onChange={e => updateSettingsDraft(field, e.target.value)} placeholder="Cole um link ou escolha uma mídia" className="flex-1 min-w-0 px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" />
          {/^https?:\/\//i.test(value) && <button type="button" disabled={uploadingKey === `${key}-url`} onClick={async () => { const path = await importImageFromUrl(value, folder, `${key}-url`, true); if (path) updateSettingsDraft(field, path); }} className="px-3 py-2 border border-background-300 hover:border-primary-400 rounded-md text-xs font-label font-semibold text-foreground-700 cursor-pointer disabled:opacity-50 whitespace-nowrap">Importar link</button>}
        </div>
        <label className="mt-2 inline-flex items-center gap-2 border border-background-300 hover:border-primary-400 px-3 py-2 rounded-md text-xs font-label font-semibold text-foreground-700 cursor-pointer">
          <i className={uploadingKey === key ? "ri-loader-4-line animate-spin" : "ri-upload-2-line"} /> Escolher mídia
          <input type="file" accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm" className="hidden" disabled={uploadingKey === key} onChange={async e => { const file = e.target.files?.[0]; if (!file) return; const path = await uploadImage(file, folder, key, true); if (path) updateSettingsDraft(field, path); }} />
        </label>
        <p className="text-foreground-400 text-[11px] mt-1">JPG, PNG, WebP e GIF; vídeos MP4 ou WebM. Imagens estáticas até 10 MB; GIFs e vídeos até 50 MB.</p>
        {value && (
          isVideoMedia(value) ? (
            <video src={value} className="mt-3 w-full max-h-40 rounded-md object-cover bg-background-100" autoPlay muted loop playsInline preload="metadata" />
          ) : (
            <img src={value} alt={label} className="mt-3 w-full max-h-40 rounded-md object-cover bg-background-100" />
          )
        )}
      </div>
    );
  };

  const renderSettings = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-lg font-heading font-bold text-foreground-950">Configurações Gerais do Site</h2>
        <button type="button" onClick={handleSaveSettings} disabled={!settingsDirty} className="flex items-center gap-2 bg-primary-500 hover:bg-primary-600 disabled:bg-background-300 disabled:text-foreground-500 text-background-50 px-4 py-2 rounded-md text-sm font-label font-semibold transition-all cursor-pointer disabled:cursor-not-allowed whitespace-nowrap">
          <i className="ri-save-line" />
          {settingsDirty ? "Salvar Configurações" : "Configurações Salvas"}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Nome do Site</label>
          <input type="text" value={settingsDraft.siteName} onChange={e => updateSettingsDraft("siteName", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" />
        </div>
        <div>
          <label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Logo do Site</label>
          <div className="flex gap-2">
            <input type="text" value={settingsDraft.logoUrl} onChange={e => updateSettingsDraft("logoUrl", e.target.value)} placeholder="/images/logo/logo.png ou link da imagem" className="flex-1 min-w-0 px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" />
            {/^https?:\/\//i.test(settingsDraft.logoUrl) && <button type="button" disabled={uploadingKey === "settings-logo-url"} onClick={async () => { const path = await importImageFromUrl(settingsDraft.logoUrl, "logo", "settings-logo-url"); if (path) updateSettingsDraft("logoUrl", path); }} className="px-3 py-2 border border-background-300 hover:border-primary-400 rounded-md text-xs font-label font-semibold text-foreground-700 cursor-pointer disabled:opacity-50 whitespace-nowrap">Importar link</button>}
          </div>
          <label className="mt-2 inline-flex items-center gap-2 border border-background-300 hover:border-primary-400 px-3 py-2 rounded-md text-xs font-label font-semibold text-foreground-700 cursor-pointer">
            <i className={uploadingKey === "settings-logo" ? "ri-loader-4-line animate-spin" : "ri-upload-2-line"} /> Escolher imagem
            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" disabled={uploadingKey === "settings-logo"} onChange={e => void handleSettingsLogoUpload(e.target.files?.[0])} />
          </label>
          <p className="text-foreground-400 text-[11px] mt-1">JPG, PNG ou WebP. Máximo 10 MB. A imagem será otimizada automaticamente.</p>
        </div>
        <div>
          <label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Cor Primária (HEX)</label>
          <div className="flex items-center gap-2"><input type="color" value={settingsDraft.primaryColor} onChange={e => updateSettingsDraft("primaryColor", e.target.value)} className="w-10 h-10 rounded border border-background-300 cursor-pointer" /><input type="text" value={settingsDraft.primaryColor} onChange={e => updateSettingsDraft("primaryColor", e.target.value)} className="flex-1 px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div>
        </div>
        <div>
          <label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Cor Secundária (HEX)</label>
          <div className="flex items-center gap-2"><input type="color" value={settingsDraft.accentColor} onChange={e => updateSettingsDraft("accentColor", e.target.value)} className="w-10 h-10 rounded border border-background-300 cursor-pointer" /><input type="text" value={settingsDraft.accentColor} onChange={e => updateSettingsDraft("accentColor", e.target.value)} className="flex-1 px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div>
        </div>
      </div>

      <div className="bg-background-100 border border-background-200 rounded-lg p-4">
        <h3 className="text-sm font-heading font-bold text-foreground-950 mb-3">Pré-visualização da Logo</h3>
        <div className="bg-background-50 border border-background-200 rounded-md p-4 flex items-center justify-center"><img src={settingsDraft.logoUrl} alt="Logo" className="max-h-24 object-contain" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} /></div>
      </div>

      <div className="border-t border-background-200 pt-6">
        <h3 className="text-base font-heading font-bold text-foreground-950 mb-1">Mídias dos Banners e Cabeçalhos</h3>
        <p className="text-foreground-500 text-xs mb-4">Você pode usar imagem estática, GIF animado ou vídeo. Vídeos são exibidos automaticamente sem som e em repetição contínua.</p>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {renderSettingsMediaField("allProductsBannerMedia", "Banner Todos os Produtos", "banners", "settings-all-products-media")}
          {renderSettingsMediaField("companyHeaderMedia", "Cabeçalho A Empresa", "empresa", "settings-company-header-media")}
          {renderSettingsMediaField("careersHeaderMedia", "Cabeçalho Trabalhe Conosco", "propostas", "settings-careers-header-media")}
        </div>
      </div>

      <div className="border-t border-background-200 pt-6">
        <h3 className="text-base font-heading font-bold text-foreground-950 mb-1">Telefones e Contato</h3>
        <p className="text-foreground-500 text-xs mb-4">Esses dados aparecem no menu, rodapé e páginas do site.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Telefone Loja Central</label><input type="text" value={settingsDraft.contactPhoneCentral} onChange={e => updateSettingsDraft("contactPhoneCentral", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div>
          <div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Telefone MZ Outlet</label><input type="text" value={settingsDraft.contactPhoneFilial} onChange={e => updateSettingsDraft("contactPhoneFilial", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div>
          <div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">WhatsApp Loja Central (somente números, ex: 551736412222)</label><input type="text" value={settingsDraft.contactWhatsapp} onChange={e => updateSettingsDraft("contactWhatsapp", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div>
          <div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">E-mail Público</label><input type="email" value={settingsDraft.contactEmail} onChange={e => updateSettingsDraft("contactEmail", e.target.value)} placeholder="vendasmez@mezsantafe.com.br" className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div>
        </div>
      </div>

      <div className="border-t border-background-200 pt-6">
        <h3 className="text-base font-heading font-bold text-foreground-950 mb-1">Horários de Atendimento</h3>
        <p className="text-foreground-500 text-xs mb-4">Exibidos no rodapé e na página de contato.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Segunda a Sexta</label><input type="text" value={settingsDraft.contactHoursWeek} onChange={e => updateSettingsDraft("contactHoursWeek", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div>
          <div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Sábado</label><input type="text" value={settingsDraft.contactHoursSat} onChange={e => updateSettingsDraft("contactHoursSat", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div>
          <div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Domingo</label><input type="text" value={settingsDraft.contactHoursSun} onChange={e => updateSettingsDraft("contactHoursSun", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div>
        </div>
      </div>

      <div className="border-t border-background-200 pt-6">
        <h3 className="text-base font-heading font-bold text-foreground-950 mb-1">Redes Sociais</h3>
        <p className="text-foreground-500 text-xs mb-4">Links exibidos no rodapé e botão WhatsApp.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Link WhatsApp</label><input type="text" value={settingsDraft.socialWhatsapp} onChange={e => updateSettingsDraft("socialWhatsapp", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div>
          <div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Link Facebook</label><input type="text" value={settingsDraft.socialFacebook} onChange={e => updateSettingsDraft("socialFacebook", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div>
          <div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Link Instagram</label><input type="text" value={settingsDraft.socialInstagram} onChange={e => updateSettingsDraft("socialInstagram", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div>
          <div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Link YouTube</label><input type="text" value={settingsDraft.socialYoutube} onChange={e => updateSettingsDraft("socialYoutube", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div>
        </div>
      </div>

      <div className="border-t border-background-200 pt-6 flex justify-end">
        <button type="button" onClick={handleSaveSettings} disabled={!settingsDirty} className="flex items-center gap-2 bg-primary-500 hover:bg-primary-600 disabled:bg-background-300 disabled:text-foreground-500 text-background-50 px-5 py-2.5 rounded-md text-sm font-label font-semibold transition-all cursor-pointer disabled:cursor-not-allowed whitespace-nowrap"><i className="ri-save-line" />{settingsDirty ? "Salvar Configurações" : "Configurações Salvas"}</button>
      </div>
    </div>
  );

  const renderBackups = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-heading font-bold text-foreground-950">Gerenciar Backups</h2>
        <p className="text-foreground-500 text-sm mt-1">O sistema manterá somente os últimos 5 backups. Quando o sexto for criado, o mais antigo será removido automaticamente.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-background-50 border border-background-200 rounded-lg p-5">
          <h3 className="font-heading font-bold text-foreground-950 text-sm mb-3">Salvar Backup Agora</h3>
          <p className="text-foreground-600 text-xs mb-4">Cria um backup manual dos dados atuais do site.</p>
          <button onClick={() => void handleManualBackup()} className="flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-4 py-2 rounded-md text-sm font-label font-semibold transition-all cursor-pointer whitespace-nowrap"><i className="ri-save-line" /> Salvar Backup Agora</button>
        </div>

        <div className="bg-background-50 border border-background-200 rounded-lg p-5">
          <h3 className="font-heading font-bold text-foreground-950 text-sm mb-3">Auto-Backup</h3>
          <p className="text-foreground-600 text-xs mb-4">Cria um backup automático se houver alterações desde o último registro.</p>
          <button onClick={() => void handleAutoBackup()} className="flex items-center gap-2 bg-secondary-500 hover:bg-secondary-600 text-background-50 px-4 py-2 rounded-md text-sm font-label font-semibold transition-all cursor-pointer whitespace-nowrap"><i className="ri-refresh-line" /> Auto-Backup Manual</button>
        </div>
      </div>

      <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
        <div className="bg-foreground-950 px-5 py-3"><h3 className="text-background-50 font-heading font-bold text-sm">Histórico de Backups</h3><p className="text-background-400 text-xs mt-0.5">Últimos backups salvos (máximo 5)</p></div>
        {backups.length === 0 ? (
          <div className="p-8 text-center"><i className="ri-database-2-line text-3xl text-foreground-300 mb-3 block" /><p className="text-foreground-500 text-sm font-label">Nenhum backup salvo ainda.</p><p className="text-foreground-400 text-xs mt-1">Clique em "Salvar Backup Agora" para criar o primeiro.</p></div>
        ) : (
          <div className="divide-y divide-background-100">
            {backups.map((bk, i) => (
              <div key={bk.id} className="flex items-center justify-between px-5 py-4">
                <div className="flex items-center gap-3"><div className={`w-8 h-8 flex items-center justify-center rounded-full ${i === 0 ? "bg-primary-100 text-primary-600" : "bg-background-200 text-foreground-500"}`}><i className={i === 0 ? "ri-save-3-fill text-sm" : "ri-save-3-line text-sm"} /></div><div><p className="font-label font-semibold text-foreground-950 text-sm">{bk.label}</p><p className="text-foreground-500 text-xs">{formatBackupDate(bk.timestamp)}</p></div></div>
                <button onClick={() => handleRestoreBackup(bk)} className="flex items-center gap-1.5 bg-accent-500 hover:bg-accent-600 text-background-50 px-3 py-1.5 rounded-md text-xs font-label font-semibold transition-all cursor-pointer whitespace-nowrap"><i className="ri-arrow-go-back-line" /> Restaurar</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderList = <T extends { id: number }>(
    items: T[],
    type: string,
    renderItem: (item: T) => ReactNode,
    showAdd = true,
  ) => (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-foreground-600 text-sm font-label">{items.length} item{items.length !== 1 ? "s" : ""}</p>
        {showAdd && <button onClick={() => openEdit(type)} className="flex items-center gap-1.5 bg-primary-500 hover:bg-primary-600 text-background-50 px-3 py-2 rounded-md text-xs font-label font-semibold transition-all cursor-pointer whitespace-nowrap"><i className="ri-add-line" /> Adicionar</button>}
      </div>
      <div className="space-y-2">
        {items.map(item => (
          <div key={item.id} className="flex items-center justify-between bg-background-50 border border-background-200 rounded-lg px-4 py-3">
            <div className="flex-1 min-w-0">{renderItem(item)}</div>
            <div className="flex items-center gap-1 ml-3 shrink-0">
              <button onClick={() => openEdit(type, item)} className="w-8 h-8 flex items-center justify-center text-foreground-500 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-all cursor-pointer" title="Editar"><i className="ri-edit-line text-sm" /></button>
              <button onClick={() => handleDelete(type, item.id)} className="w-8 h-8 flex items-center justify-center text-foreground-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-all cursor-pointer" title="Excluir"><i className="ri-delete-bin-line text-sm" /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="bg-background-50 border border-dashed border-background-300 rounded-lg p-8 text-center text-foreground-500 text-sm font-label">Nenhum item cadastrado.</div>}
      </div>
    </div>
  );

  const renderEditModal = () => {
    if (!editing) return null;

    const renderImageField = (field: string, label: string, folder: string, allowBannerMedia = false) => {
      const key = `${editType}-${field}`;
      const value = editing[field] || "";
      const accept = allowBannerMedia
        ? "image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm"
        : "image/jpeg,image/png,image/webp";

      return (
        <div>
          <label className="block text-xs font-label font-semibold text-foreground-700 mb-1">{label}</label>
          <div className="flex gap-2">
            <input type="text" value={value} onChange={e => updateField(field, e.target.value)} placeholder="Cole um link ou escolha uma imagem" className="flex-1 min-w-0 px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" />
            {/^https?:\/\//i.test(value) && <button type="button" disabled={uploadingKey === key} onClick={async () => { const path = await importImageFromUrl(value, folder, key, allowBannerMedia); if (path) updateField(field, path); }} className="px-3 py-2 border border-background-300 hover:border-primary-400 rounded-md text-xs font-label font-semibold text-foreground-700 cursor-pointer disabled:opacity-50 whitespace-nowrap">Importar link</button>}
          </div>
          <label className="mt-2 inline-flex items-center gap-2 border border-background-300 hover:border-primary-400 px-3 py-2 rounded-md text-xs font-label font-semibold text-foreground-700 cursor-pointer">
            <i className={uploadingKey === key ? "ri-loader-4-line animate-spin" : "ri-upload-2-line"} /> {allowBannerMedia ? "Escolher mídia" : "Escolher imagem"}
            <input type="file" accept={accept} className="hidden" disabled={uploadingKey === key} onChange={async e => { const file = e.target.files?.[0]; if (!file) return; const path = await uploadImage(file, folder, key, allowBannerMedia); if (path) updateField(field, path); }} />
          </label>
          <p className="text-foreground-400 text-[11px] mt-1">{allowBannerMedia ? "JPG, PNG, WebP, GIF, MP4 ou WebM. Imagens estáticas até 10 MB; GIFs e vídeos até 50 MB." : "JPG, PNG ou WebP. Tamanho máximo: 10 MB. As imagens serão otimizadas automaticamente para o site."}</p>
          {value && (allowBannerMedia && isVideoMedia(value) ? <video src={value} className="mt-2 max-h-32 rounded-md object-cover" autoPlay muted loop playsInline preload="metadata" /> : <img src={value} alt="Preview" className="mt-2 max-h-32 rounded-md object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />)}
        </div>
      );
    };

    const renderTextareaField = (field: string, label: string, rows = 4) => (
      <div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">{label}</label><textarea value={editing[field] || ""} onChange={e => updateField(field, e.target.value)} rows={rows} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400 resize-y" /></div>
    );

    const modalTitle = editType === "newsItem" ? "Notícia" : editType === "rssSource" ? "Fonte RSS" : editType === "company" ? "Nossa História / Empresa" : editType === "companyConstruSete" ? "ConstruSete" : editType === "companyTimeline" ? "Acontecimento da Trajetória" : editType === "testimonial" ? "Depoimento" : editType === "store" ? "Loja" : editType === "category" ? "Categoria" : editType === "product" ? "Produto" : editType === "seller" ? "Vendedor" : editType === "sellerAvailability" ? "Afastamento do Vendedor" : editType === "banner" ? "Banner" : editType === "brand" ? "Marca" : editType === "jobVacancy" ? "Vaga" : editType === "raffle" ? "Sorteio" : "Item";

    return (
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/50" onClick={closeEdit}>
        <div className={`bg-background-50 rounded-xl border border-background-200 w-full ${editType === "raffle" ? "max-w-3xl" : "max-w-lg"} max-h-[88vh] overflow-y-auto`} onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between px-5 py-4 border-b border-background-200 sticky top-0 bg-background-50 z-10">
            <h3 className="font-heading font-bold text-foreground-950 text-base">{modalTitle}</h3>
            <button onClick={closeEdit} className="w-8 h-8 flex items-center justify-center text-foreground-400 hover:text-foreground-700 rounded-full cursor-pointer"><i className="ri-close-line" /></button>
          </div>

          <div className="p-5 space-y-4">
            {editType === "banner" && <><div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Título</label><input type="text" value={editing.title} onChange={e => updateField("title", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div><div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Subtítulo</label><input type="text" value={editing.subtitle} onChange={e => updateField("subtitle", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div>{renderTextareaField("description", "Descrição")}<div className="grid grid-cols-2 gap-3"><div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Texto CTA</label><input type="text" value={editing.ctaText} onChange={e => updateField("ctaText", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div><div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Link CTA</label><input type="text" value={editing.ctaLink} onChange={e => updateField("ctaLink", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div></div>{renderImageField("image", "Mídia do Banner", "banners", true)}</>}

            {editType === "brand" && <><div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Nome da Marca</label><input type="text" value={editing.name} onChange={e => updateField("name", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div>{renderImageField("logo", "Logo da Marca", "marcas")}</>}

            {editType === "category" && <><div className="grid grid-cols-2 gap-3"><div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Nome</label><input type="text" value={editing.name} onChange={e => updateField("name", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div><div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Ícone Remix</label><input type="text" value={editing.icon} onChange={e => updateField("icon", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div></div>{renderImageField("image", "Imagem da Categoria", "categorias")}<div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Subcategorias</label>{(editing.subcategories || []).map((sub: any, idx: number) => <div key={sub.id || idx} className="flex items-center gap-2 mb-1"><input type="text" value={sub.name || ""} onChange={e => updateNestedField("subcategories", idx, { ...sub, name: e.target.value })} className="flex-1 px-3 py-1.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /><button type="button" onClick={() => removeNestedField("subcategories", idx)} className="w-7 h-7 flex items-center justify-center text-red-500 hover:bg-red-50 rounded cursor-pointer"><i className="ri-close-line text-sm" /></button></div>)}<button type="button" onClick={() => addNestedField("subcategories")} className="text-xs text-primary-500 font-label mt-1 cursor-pointer hover:underline">+ Adicionar subcategoria</button></div></>}

            {editType === "product" && <><div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Nome</label><input type="text" value={editing.name} onChange={e => updateField("name", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div><div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Código *</label><input type="text" required value={editing.code || ""} onChange={e => updateField("code", e.target.value)} placeholder="Ex.: 12345, ABC-001 ou código interno" className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /><p className="text-foreground-400 text-[11px] mt-1">Obrigatório. Este código será exibido no site para facilitar a identificação do produto.</p></div><div className="grid grid-cols-2 gap-3"><div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Marca</label><input type="text" value={editing.brand} onChange={e => updateField("brand", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div><div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Preço (R$)</label><input type="number" value={editing.price} onChange={e => updateField("price", parseFloat(e.target.value) || 0)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div></div><div className="grid grid-cols-1 md:grid-cols-2 gap-3"><div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Categoria *</label><select required value={Number(editing.category) || 0} onChange={e => { const categoryId = Number(e.target.value) || 0; setEditing((prev: any) => ({ ...prev, category: categoryId, subcategoryId: 0 })); }} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400 cursor-pointer"><option value={0}>Selecione a categoria</option>{data.categories.map((category: Category) => <option key={category.id} value={category.id}>{category.name}</option>)}</select></div><div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Subcategoria *</label><select required value={Number(editing.subcategoryId) || 0} disabled={!Number(editing.category)} onChange={e => updateField("subcategoryId", Number(e.target.value) || 0)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400 cursor-pointer disabled:bg-background-100 disabled:text-foreground-400 disabled:cursor-not-allowed"><option value={0}>{Number(editing.category) ? "Selecione a subcategoria" : "Selecione primeiro a categoria"}</option>{(data.categories.find((category: Category) => category.id === Number(editing.category))?.subcategories || []).map(subcategory => <option key={subcategory.id} value={subcategory.id}>{subcategory.name}</option>)}</select><p className="text-foreground-400 text-[11px] mt-1">As opções são carregadas conforme a categoria selecionada.</p></div></div><div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Slug (URL)</label><input type="text" value={editing.slug} onChange={e => updateField("slug", e.target.value)} placeholder="Deixe vazio para gerar automaticamente pelo nome" className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /><p className="text-foreground-400 text-[11px] mt-1">Se ficar vazio, o sistema cria automaticamente uma URL válida ao salvar.</p></div><div className="flex flex-wrap items-center gap-4"><label className="flex items-center gap-2 text-sm font-label text-foreground-700 cursor-pointer"><input type="checkbox" checked={editing.showPrice} onChange={e => updateField("showPrice", e.target.checked)} className="rounded" /> Exibir preço</label><label className="flex items-center gap-2 text-sm font-label text-foreground-700 cursor-pointer"><input type="checkbox" checked={editing.isPromo} onChange={e => updateField("isPromo", e.target.checked)} className="rounded" /> Produto promocional</label></div>{editing.isPromo && <div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Label Promo</label><input type="text" value={editing.promoLabel} onChange={e => updateField("promoLabel", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div>}{renderTextareaField("description", "Descrição")}<div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Especificações</label>{(editing.specs || []).map((spec: string, idx: number) => <div key={idx} className="flex items-center gap-2 mb-1"><input type="text" value={spec} onChange={e => updateNestedField("specs", idx, e.target.value)} className="flex-1 px-3 py-1.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /><button type="button" onClick={() => removeNestedField("specs", idx)} className="w-7 h-7 flex items-center justify-center text-red-500 hover:bg-red-50 rounded cursor-pointer"><i className="ri-close-line text-sm" /></button></div>)}<button type="button" onClick={() => addNestedField("specs")} className="text-xs text-primary-500 font-label mt-1 cursor-pointer hover:underline">+ Adicionar especificação</button></div><div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Imagens do Produto</label>{(editing.images || []).map((image: string, idx: number) => { const key = `product-image-${idx}`; return <div key={idx} className="border border-background-200 rounded-md p-3 mb-2"><div className="flex gap-2"><input type="text" value={image} onChange={e => updateNestedField("images", idx, e.target.value)} placeholder="Cole um link ou escolha uma imagem" className="flex-1 min-w-0 px-3 py-1.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" />{ /^https?:\/\//i.test(image) && <button type="button" onClick={async () => { const path = await importImageFromUrl(image, "produtos", `${key}-url`); if (path) updateNestedField("images", idx, path); }} className="px-2 py-1.5 border border-background-300 hover:border-primary-400 rounded text-[11px] font-label font-semibold text-foreground-700 cursor-pointer whitespace-nowrap">Importar link</button>}<button type="button" onClick={() => removeNestedField("images", idx)} className="w-8 h-8 flex items-center justify-center text-red-500 hover:bg-red-50 rounded cursor-pointer"><i className="ri-delete-bin-line text-sm" /></button></div><label className="mt-2 inline-flex items-center gap-2 border border-background-300 hover:border-primary-400 px-3 py-1.5 rounded-md text-xs font-label font-semibold text-foreground-700 cursor-pointer"><i className={uploadingKey === key ? "ri-loader-4-line animate-spin" : "ri-upload-2-line"} /> Escolher imagem<input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" disabled={uploadingKey === key} onChange={async e => { const file = e.target.files?.[0]; if (!file) return; const path = await uploadImage(file, "produtos", key); if (path) updateNestedField("images", idx, path); }} /></label><p className="text-foreground-400 text-[11px] mt-1">JPG, PNG ou WebP. Máximo 10 MB. Otimização automática.</p>{image && <img src={image} alt="Preview" className="mt-2 max-h-28 rounded object-cover" />}</div>; })}<button type="button" onClick={() => addNestedField("images")} className="text-xs text-primary-500 font-label mt-1 cursor-pointer hover:underline">+ Adicionar imagem</button></div></>}

            {editType === "sellerAvailability" && <><div className="bg-amber-50 border border-amber-200 rounded-lg p-4"><p className="text-amber-900 text-sm font-label font-semibold">Afastar {editing.name}</p><p className="text-amber-800 text-xs mt-1">Escolha a data em que o vendedor deverá voltar automaticamente a ficar disponível no site.</p></div><div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Data de retorno</label><input type="date" min={todayString()} value={editing.inactiveUntil || ""} onChange={e => updateField("inactiveUntil", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div></>}

            {editType === "seller" && <><div className="grid grid-cols-2 gap-3"><div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Nome</label><input type="text" value={editing.name} onChange={e => updateField("name", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div><div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Ocupação / Cargo</label><input type="text" value={editing.role} onChange={e => updateField("role", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div></div><div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Loja</label><select value={editing.store || "central"} onChange={e => updateField("store", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400 cursor-pointer">{storeOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}</select></div><div className="grid grid-cols-2 gap-3"><div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Telefone</label><input type="text" value={editing.phone} onChange={e => updateField("phone", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div><div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">WhatsApp</label><input type="text" value={editing.whatsapp} onChange={e => updateField("whatsapp", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div></div>{renderImageField("photo", "Foto do Vendedor", "vendedores")}<label className="flex items-center gap-2 text-sm font-label text-foreground-700 cursor-pointer"><input type="checkbox" checked={editing.available} onChange={e => updateField("available", e.target.checked)} className="rounded" /> Disponível para contato</label>{!editing.available && <div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Data de retorno</label><input type="date" min={todayString()} value={editing.inactiveUntil || ""} onChange={e => updateField("inactiveUntil", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div>}</>}

            {editType === "newsItem" && <><div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Título</label><input type="text" value={editing.title} onChange={e => updateField("title", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div>{renderTextareaField("excerpt", "Resumo")}<div className="grid grid-cols-3 gap-3"><div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Categoria</label><input type="text" value={editing.category} onChange={e => updateField("category", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div><div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Data</label><input type="date" value={editing.date} onChange={e => updateField("date", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div><div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Fonte</label><input type="text" value={editing.source} onChange={e => updateField("source", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div></div>{renderImageField("image", "Imagem da Notícia", "noticias")}</>}

            {editType === "rssSource" && <><div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Nome da Fonte</label><input type="text" value={editing.name} onChange={e => updateField("name", e.target.value)} placeholder="Ex: Mapa da Obra" className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /><p className="text-foreground-400 text-xs mt-1">Nome exibido como origem das notícias.</p></div><div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Link do Feed RSS</label><input type="url" value={editing.url} onChange={e => updateField("url", e.target.value)} placeholder="https://www.exemplo.com.br/feed/" className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div><label className="flex items-center gap-2 text-sm font-label text-foreground-700 cursor-pointer"><input type="checkbox" checked={editing.enabled} onChange={e => updateField("enabled", e.target.checked)} className="rounded" /> Fonte ativa (buscar notícias)</label></>}

            {editType === "store" && <><div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Nome</label><input type="text" value={editing.name} onChange={e => updateField("name", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div><div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Endereço</label><input type="text" value={editing.address} onChange={e => updateField("address", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div><div className="grid grid-cols-2 gap-3"><div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Cidade</label><input type="text" value={editing.city} onChange={e => updateField("city", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div><div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">CEP</label><input type="text" value={editing.cep} onChange={e => updateField("cep", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div></div><div className="grid grid-cols-2 gap-3"><div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Telefone</label><input type="text" value={editing.phone} onChange={e => updateField("phone", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div><div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">WhatsApp</label><input type="text" value={editing.whatsapp} onChange={e => updateField("whatsapp", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div></div><div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Horários</label><input type="text" value={editing.hours} onChange={e => updateField("hours", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div><div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">URL Google Maps Embed</label><input type="text" value={editing.mapUrl} onChange={e => updateField("mapUrl", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div></>}

            {editType === "company" && <><div className="bg-background-100 border border-background-200 rounded-lg p-3"><p className="text-xs text-foreground-600 font-label">Edite aqui a apresentação da empresa. A linha do tempo "Nossa Trajetória" é gerenciada separadamente na tela principal desta seção.</p></div><div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Título da História</label><input type="text" value={editing.historyTitle} onChange={e => updateField("historyTitle", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div><div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Ano Fundação</label><input type="number" value={editing.foundedYear} onChange={e => updateField("foundedYear", parseInt(e.target.value) || 2011)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div>{renderTextareaField("historyText", "Nossa História", 7)}{renderTextareaField("mission", "Missão")}{renderTextareaField("vision", "Visão")}{renderTextareaField("meta", "Meta")}</>}

            {editType === "companyConstruSete" && <>
              <div className="bg-background-100 border border-background-200 rounded-lg p-3">
                <p className="text-xs text-foreground-600 font-label">Edite aqui todo o conteúdo da seção ConstruSete exibida no final da página A Empresa.</p>
              </div>

              <label className="flex items-center gap-2 text-sm font-label text-foreground-700 cursor-pointer">
                <input type="checkbox" checked={editing.construseteActive !== false} onChange={e => updateField("construseteActive", e.target.checked)} className="rounded" />
                Exibir seção ConstruSete na página A Empresa
              </label>

              {renderImageField("construseteLogo", "Logo da ConstruSete", "empresa")}

              <div>
                <label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Chamada / Selo</label>
                <input type="text" value={editing.construseteBadge || ""} onChange={e => updateField("construseteBadge", e.target.value)} placeholder="Ex.: Associativismo que fortalece" className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" />
              </div>

              <div>
                <label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Título</label>
                <input type="text" value={editing.construseteTitle || ""} onChange={e => updateField("construseteTitle", e.target.value)} placeholder="Ex.: M&Z e ConstruSete" className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" />
              </div>

              {renderTextareaField("construseteText", "Texto / Apresentação", 7)}

              <div>
                <label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Link da ConstruSete</label>
                <input type="url" value={editing.construseteUrl || ""} onChange={e => updateField("construseteUrl", e.target.value)} placeholder="https://www.construsete.com.br/" className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" />
                <p className="text-foreground-400 text-[11px] mt-1">Se preenchido, a logo da ConstruSete ficará clicável e abrirá esse endereço em uma nova aba.</p>
              </div>
            </>}

            {editType === "companyTimeline" && <><div className="bg-background-100 border border-background-200 rounded-lg p-3"><p className="text-xs text-foreground-600 font-label">Cadastre um acontecimento da seção "Nossa Trajetória". Os acontecimentos são organizados automaticamente pelo ano.</p></div><div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Ano</label><input type="number" min="1900" max="2200" value={editing.eventYear} onChange={e => updateField("eventYear", parseInt(e.target.value) || new Date().getFullYear())} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div><div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Título do Acontecimento</label><input type="text" value={editing.title} onChange={e => updateField("title", e.target.value)} placeholder="Ex: Primeira Expansão" className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div>{renderTextareaField("description", "Descrição", 5)}</>}

            {editType === "testimonial" && <>
              <div>
                <label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Nome</label>
                <input type="text" value={editing.name} onChange={e => updateField("name", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" />
              </div>

              <div>
                <p className="block text-xs font-label font-semibold text-foreground-700 mb-1">Miniatura atual</p>
                {editing.photo ? (
                  <div className="w-20 h-20 rounded-full overflow-hidden border border-background-300 bg-background-100">
                    <img
                      src={editing.photo}
                      alt={editing.name || "Foto do cliente"}
                      className="w-full h-full object-cover object-top"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  </div>
                ) : (
                  <div className="flex items-center gap-3 bg-background-100 border border-background-200 rounded-md p-3">
                    <div className="w-12 h-12 rounded-full bg-background-200 flex items-center justify-center text-foreground-400 shrink-0">
                      <i className="ri-user-line text-xl" />
                    </div>
                    <p className="text-xs text-foreground-500 font-label">Nenhuma foto cadastrada neste depoimento.</p>
                  </div>
                )}
              </div>

              {renderImageField("photo", "Foto do Cliente", "depoimentos")}
              {renderTextareaField("text", "Depoimento")}
              <div>
                <label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Estrelas (1-5)</label>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map(star => (
                    <button type="button" key={star} onClick={() => updateField("stars", star)} className={`text-lg cursor-pointer ${star <= editing.stars ? "text-yellow-500" : "text-foreground-300"}`}>
                      <i className={star <= editing.stars ? "ri-star-fill" : "ri-star-line"} />
                    </button>
                  ))}
                </div>
              </div>
            </>}

            {editType === "jobVacancy" && <><div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Nome da Vaga</label><input type="text" value={editing.title} onChange={e => updateField("title", e.target.value)} placeholder="Ex: Consultor de Vendas" className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div><div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Tipo / Local</label><input type="text" value={editing.type} onChange={e => updateField("type", e.target.value)} placeholder="Ex: Loja Central • CLT" className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div>{renderTextareaField("description", "Descrição da Vaga")}<div className="flex flex-wrap gap-4"><label className="flex items-center gap-2 text-sm font-label text-foreground-700 cursor-pointer"><input type="checkbox" checked={editing.urgent} onChange={e => updateField("urgent", e.target.checked)} className="rounded" /> Marcar como urgente</label><label className="flex items-center gap-2 text-sm font-label text-foreground-700 cursor-pointer"><input type="checkbox" checked={editing.active} onChange={e => updateField("active", e.target.checked)} className="rounded" /> Vaga ativa no site</label></div></>}

            {editType === "raffle" && <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Título do Sorteio</label>
                  <input type="text" value={editing.title} onChange={e => updateField("title", e.target.value)} maxLength={255} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" />
                </div>
                <div className="flex items-end gap-4 pb-2">
                  <label className="flex items-center gap-2 text-sm font-label text-foreground-700 cursor-pointer"><input type="checkbox" checked={editing.active} onChange={e => updateField("active", e.target.checked)} className="rounded" /> Sorteio vigente/ativo</label>
                  <label className="flex items-center gap-2 text-sm font-label text-foreground-700 cursor-pointer"><input type="checkbox" checked={editing.visibleAfterEnd} onChange={e => updateField("visibleAfterEnd", e.target.checked)} className="rounded" /> Mostrar após encerrar</label>
                </div>
              </div>

              {renderTextareaField("description", "Descrição do Sorteio")}
              {renderImageField("bannerImage", "Mídia do Banner do Sorteio", "sorteios", true)}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Data Inicial</label><input type="date" value={editing.startDate || ""} onChange={e => updateField("startDate", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div>
                <div><label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Data Final</label><input type="date" value={editing.endDate || ""} min={editing.startDate || undefined} onChange={e => updateField("endDate", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /></div>
              </div>

              <div>
                <label className="block text-xs font-label font-semibold text-foreground-700 mb-1">Pergunta principal para participação (opcional)</label>
                <input type="text" value={editing.question || ""} onChange={e => updateField("question", e.target.value)} maxLength={1000} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" />
              </div>

              <div className="border-t border-background-200 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <div><h4 className="font-heading font-bold text-foreground-950 text-sm">Campos adicionais do cadastro</h4><p className="text-foreground-400 text-[11px] mt-0.5">Nome, CPF, E-mail e WhatsApp são campos fixos e sempre validados. Crie abaixo somente os campos extras.</p></div>
                  <button type="button" onClick={addRaffleField} className="text-xs text-primary-600 font-label font-semibold cursor-pointer"><i className="ri-add-line" /> Adicionar campo</button>
                </div>

                <div className="space-y-3">
                  {(editing.fields || []).map((field: RaffleField, idx: number) => (
                    <div key={field.id} className="bg-background-100 border border-background-200 rounded-lg p-4 space-y-3">
                      <div className="flex justify-between gap-3">
                        <span className="text-xs font-label font-semibold text-foreground-600">Campo {idx + 1}</span>
                        <button type="button" onClick={() => removeRaffleField(idx)} className="text-red-500 text-xs font-label cursor-pointer"><i className="ri-delete-bin-line" /> Remover campo</button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <input type="text" value={field.label} onChange={e => updateRaffleNested("fields", idx, "label", e.target.value)} placeholder="Nome do campo (ex.: Cidade, Nº do Cupom)" maxLength={255} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" />
                        <select value={field.fieldType} onChange={e => updateRaffleNested("fields", idx, "fieldType", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400">
                          <option value="text">Texto</option><option value="name">Nome / somente letras</option><option value="number">Número</option><option value="email">E-mail</option><option value="phone">Telefone / WhatsApp</option><option value="cpf">CPF</option><option value="date">Data</option><option value="select">Lista / Seleção</option><option value="textarea">Texto longo</option>
                        </select>
                      </div>
                      {field.fieldType === "select" && <div><label className="block text-[11px] font-label font-semibold text-foreground-600 mb-1">Opções — uma por linha</label><textarea rows={4} value={(field.options || []).join("\n")} onChange={e => updateRaffleNested("fields", idx, "options", e.target.value.split(/\r?\n/).map(option => option.trim()).filter(Boolean))} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400 resize-y" /></div>}
                      <div className="flex flex-wrap gap-4">
                        <label className="flex items-center gap-2 text-xs font-label text-foreground-700 cursor-pointer"><input type="checkbox" checked={field.required} onChange={e => updateRaffleNested("fields", idx, "required", e.target.checked)} /> Obrigatório</label>
                        <label className="flex items-center gap-2 text-xs font-label text-foreground-700 cursor-pointer"><input type="checkbox" checked={field.showOnTicket} onChange={e => updateRaffleNested("fields", idx, "showOnTicket", e.target.checked)} /> Mostrar no cartão/PDF da urna</label>
                        <label className="flex items-center gap-2 text-xs font-label text-foreground-700 cursor-pointer"><input type="checkbox" checked={field.active} onChange={e => updateRaffleNested("fields", idx, "active", e.target.checked)} /> Campo ativo</label>
                      </div>
                    </div>
                  ))}
                  {(editing.fields || []).length === 0 && <p className="text-foreground-400 text-xs font-label">Nenhum campo adicional. O formulário continuará com Nome, CPF, E-mail, WhatsApp e a pergunta principal, se informada.</p>}
                </div>
              </div>

              {renderTextareaField("regulation", "Regulamento", 10)}

              <div className="border-t border-background-200 pt-4">
                <div className="flex items-center justify-between mb-3"><h4 className="font-heading font-bold text-foreground-950 text-sm">Itens / Prêmios do Sorteio</h4><button type="button" onClick={addRafflePrize} className="text-xs text-primary-600 font-label font-semibold cursor-pointer"><i className="ri-add-line" /> Adicionar outro prêmio</button></div>
                <div className="space-y-3">
                  {editing.prizes.map((prize: any, idx: number) => { const key = `raffle-prize-${idx}`; return <div key={prize.id} className="bg-background-100 border border-background-200 rounded-lg p-4 space-y-3"><div className="flex items-center justify-between"><span className="text-xs font-label font-semibold text-foreground-600">Prêmio {idx + 1}</span><button type="button" onClick={() => removeRafflePrize(idx)} className="text-red-500 text-xs font-label cursor-pointer"><i className="ri-delete-bin-line" /> Remover prêmio</button></div><input type="text" value={prize.title} onChange={e => updateRaffleNested("prizes", idx, "title", e.target.value)} placeholder="Nome do prêmio (ex.: Carriola)" maxLength={255} className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" /><textarea value={prize.description} onChange={e => updateRaffleNested("prizes", idx, "description", e.target.value)} rows={2} placeholder="Descrição do prêmio" className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400 resize-y" /><div className="flex gap-2"><input type="text" value={prize.image} onChange={e => updateRaffleNested("prizes", idx, "image", e.target.value)} placeholder="Link ou caminho da imagem" className="flex-1 min-w-0 px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400" />{/^https?:\/\//i.test(prize.image) && <button type="button" onClick={async () => { const path = await importImageFromUrl(prize.image, "sorteios", `${key}-url`); if (path) updateRaffleNested("prizes", idx, "image", path); }} className="px-2 py-1.5 border border-background-300 hover:border-primary-400 rounded text-[11px] font-label font-semibold text-foreground-700 cursor-pointer whitespace-nowrap">Importar link</button>}</div><label className="inline-flex items-center gap-2 border border-background-300 hover:border-primary-400 px-3 py-1.5 rounded-md text-xs font-label font-semibold text-foreground-700 cursor-pointer"><i className={uploadingKey === key ? "ri-loader-4-line animate-spin" : "ri-upload-2-line"} /> Escolher imagem<input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" disabled={uploadingKey === key} onChange={async e => { const file = e.target.files?.[0]; if (!file) return; const path = await uploadImage(file, "sorteios", key); if (path) updateRaffleNested("prizes", idx, "image", path); }} /></label><p className="text-foreground-400 text-[11px]">Máximo 10 MB. Otimização automática.</p>{prize.image && <img src={prize.image} alt={`Prévia do prêmio ${idx + 1}`} className="mt-2 max-h-32 max-w-full rounded-md border border-background-200 object-contain bg-background-50" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />}</div>; })}
                </div>
              </div>

              <div className="border-t border-background-200 pt-4 bg-accent-50 rounded-lg p-4">
                <p className="text-accent-900 text-xs font-label"><i className="ri-team-line mr-1" /> Os ganhadores não são digitados manualmente. Salve o sorteio e use o botão <strong>Concorrentes / Ganhadores</strong> para pesquisar uma pessoa cadastrada e vinculá-la ao prêmio correto.</p>
              </div>
            </>}
          </div>

          <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-background-200 sticky bottom-0 bg-background-50">
            <button onClick={closeEdit} className="px-4 py-2 text-sm font-label text-foreground-600 hover:text-foreground-900 cursor-pointer whitespace-nowrap">Cancelar</button>
            <button onClick={handleSave} disabled={saving} className="bg-primary-500 hover:bg-primary-600 text-background-50 px-5 py-2 rounded-md text-sm font-label font-semibold transition-all cursor-pointer whitespace-nowrap disabled:opacity-60">{saving ? "Salvando..." : "Salvar"}</button>
          </div>
        </div>
      </div>
    );
  };

  const renderRaffleManagerModal = () => {
    if (!raffleManagerId) return null;

    const raffle = data.raffles.find(item => item.id === raffleManagerId);
    if (!raffle) return null;

    return (
      <div className="fixed inset-0 z-[350] bg-black/60 flex items-center justify-center p-3 md:p-6" onClick={closeRaffleManager}>
        <div className="w-full max-w-6xl max-h-[94vh] overflow-y-auto bg-background-50 border border-background-200 rounded-xl shadow-xl" onClick={event => event.stopPropagation()}>
          <div className="sticky top-0 z-10 bg-background-50 border-b border-background-200 px-5 py-4 flex items-center justify-between gap-4">
            <div><p className="text-xs text-primary-600 font-label font-bold uppercase tracking-wide">Concorrentes e Ganhadores</p><h3 className="font-heading font-bold text-foreground-950 text-lg">{raffle.title}</h3></div>
            <button type="button" onClick={closeRaffleManager} className="w-9 h-9 rounded-full bg-background-100 hover:bg-background-200 flex items-center justify-center cursor-pointer"><i className="ri-close-line text-lg" /></button>
          </div>

          <div className="p-5 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="bg-primary-50 border border-primary-100 rounded-lg p-4"><p className="text-xs text-primary-700 font-label font-semibold">Concorrentes</p><p className="text-2xl font-heading font-bold text-primary-800 mt-1">{raffleEntriesTotal}</p></div>
              <a href={`/api/sorteio-admin.php?action=excel&raffle_id=${raffle.id}`} className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3 hover:border-green-400 transition-colors" target="_blank" rel="noopener noreferrer"><i className="ri-file-excel-2-line text-3xl text-green-700" /><div><p className="font-label font-bold text-green-800 text-sm">Exportar Excel</p><p className="text-green-700 text-xs mt-0.5">Uma pessoa por linha e cada campo em sua coluna.</p></div></a>
              <a href={`/api/sorteio-admin.php?action=pdf&raffle_id=${raffle.id}`} className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3 hover:border-red-400 transition-colors" target="_blank" rel="noopener noreferrer"><i className="ri-file-pdf-2-line text-3xl text-red-700" /><div><p className="font-label font-bold text-red-800 text-sm">PDF para Urna</p><p className="text-red-700 text-xs mt-0.5">Cartões prontos para imprimir, recortar e colocar na urna.</p></div></a>
            </div>

            <div className="border border-background-200 rounded-lg p-4">
              <div className="flex items-center justify-between gap-3 mb-3"><div><h4 className="font-heading font-bold text-foreground-950">Ganhadores já definidos</h4><p className="text-foreground-500 text-xs mt-1">Cada prêmio pode ser vinculado a um concorrente cadastrado.</p></div></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {raffleAdminWinners.map(winner => (
                  <div key={winner.id} className="bg-background-100 border border-background-200 rounded-lg p-3 flex items-center gap-3">
                    <div className="w-14 h-14 rounded-full overflow-hidden bg-background-200 shrink-0">{winner.photo ? <img src={winner.photo} alt={winner.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-foreground-400"><i className="ri-user-star-line text-2xl" /></div>}</div>
                    <div className="flex-1 min-w-0"><p className="font-label font-semibold text-foreground-950 text-sm">{winner.name}</p><p className="text-primary-600 text-xs mt-0.5">{winner.prize}</p><div className="flex flex-wrap gap-2 mt-2"><label className="text-[11px] font-label font-semibold text-foreground-600 border border-background-300 rounded px-2 py-1 cursor-pointer"><i className="ri-camera-line mr-1" /> Foto<input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={event => void handleWinnerPhotoUpload(winner.id, event.target.files?.[0])} /></label><button type="button" onClick={() => handleRemoveWinner(winner.id, winner.name)} className="text-[11px] font-label font-semibold text-red-600 border border-red-200 rounded px-2 py-1 cursor-pointer">Desfazer</button></div></div>
                  </div>
                ))}
                {raffleAdminWinners.length === 0 && <p className="text-foreground-400 text-xs font-label md:col-span-2">Nenhum ganhador definido ainda.</p>}
              </div>
            </div>

            <div>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-3">
                <div><h4 className="font-heading font-bold text-foreground-950">Lista de concorrentes</h4><p className="text-foreground-500 text-xs mt-1">Dados disponíveis somente dentro do painel administrativo.</p></div>
                <div className="relative w-full md:w-96"><i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" /><input type="search" value={raffleEntrySearch} onChange={event => setRaffleEntrySearch(event.target.value)} placeholder="Buscar nome, CPF, e-mail ou WhatsApp..." className="w-full pl-9 pr-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 font-label focus:outline-none focus:border-primary-400" /></div>
              </div>

              {raffleEntriesLoading ? <div className="py-10 text-center text-foreground-500 text-sm font-label"><i className="ri-loader-4-line animate-spin mr-2" />Carregando concorrentes...</div> : (
                <div className="space-y-3">
                  {raffleEntries.map(entry => (
                    <div key={entry.id} className="border border-background-200 rounded-lg p-4">
                      <div className="flex flex-col xl:flex-row xl:items-start gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2"><p className="font-label font-bold text-foreground-950 text-sm">#{entry.id} · {entry.name}</p>{raffleAdminWinners.some(winner => Number(winner.entryId) === entry.id) && <span className="bg-green-100 text-green-700 text-[10px] font-label font-bold px-2 py-0.5 rounded-full">GANHADOR</span>}</div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-1 mt-2 text-xs font-label text-foreground-600"><span><strong>CPF:</strong> {entry.cpf}</span><span><strong>E-mail:</strong> {entry.email}</span><span><strong>WhatsApp:</strong> {entry.whatsapp}</span>{raffle.question && <span className="sm:col-span-2 lg:col-span-3"><strong>{raffle.question}:</strong> {entry.answer}</span>}{entry.values.map(value => <span key={`${entry.id}-${value.fieldId}`}><strong>{value.label}:</strong> {value.value}</span>)}</div>
                        </div>
                        <div className="w-full xl:w-80 flex flex-col sm:flex-row xl:flex-col gap-2 shrink-0"><select value={winnerPrizeByEntry[entry.id] || ""} onChange={event => setWinnerPrizeByEntry(prev => ({ ...prev, [entry.id]: event.target.value }))} className="flex-1 px-3 py-2 text-xs border border-background-300 rounded-md bg-background-50 font-label focus:outline-none focus:border-primary-400"><option value="">Selecionar prêmio ganho...</option>{raffle.prizes.map(prize => { const currentWinner = raffleAdminWinners.find(winner => Number(winner.prizeId) === prize.id); return <option key={prize.id} value={prize.id}>{prize.title}{currentWinner ? ` — atual: ${currentWinner.name}` : ""}</option>; })}</select><button type="button" onClick={() => handleDefineWinner(entry)} className="bg-primary-500 hover:bg-primary-600 text-background-50 px-3 py-2 rounded-md text-xs font-label font-semibold cursor-pointer whitespace-nowrap">Definir ganhador</button></div>
                      </div>
                    </div>
                  ))}

                  {raffleEntries.length === 0 && <div className="border border-dashed border-background-300 rounded-lg p-8 text-center text-foreground-500 text-sm font-label">{raffleEntrySearch ? "Nenhum concorrente encontrado para essa busca." : "Ainda não há concorrentes cadastrados neste sorteio."}</div>}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSection = () => {
    switch (section) {
      case "settings": return renderSettings();
      case "backups": return renderBackups();
      case "banners": return renderList(data.banners, "banner", (b: Banner) => <div><p className="font-label font-semibold text-foreground-950 text-sm">{b.title}</p><p className="text-foreground-500 text-xs truncate">{b.subtitle}</p></div>);
      case "brands": return renderList(data.brands, "brand", (b: Brand) => <p className="font-label font-semibold text-foreground-950 text-sm">{b.name}</p>);
      case "categories": return renderList(data.categories, "category", (c: Category) => <div className="flex items-center gap-3"><div className="w-7 h-7 flex items-center justify-center text-foreground-400"><i className={`${c.icon} text-sm`} /></div><span className="font-label font-semibold text-foreground-950 text-sm">{c.name}</span></div>);
      case "products": return renderProducts();
      case "sellers": return renderSellers();
      case "news": return renderNews();
      case "stores": return renderList(data.stores, "store", (s: Store) => <div><p className="font-label font-semibold text-foreground-950 text-sm">{s.name}</p><p className="text-foreground-500 text-xs">{s.address} - {s.city}</p></div>);
      case "outlet": return renderOutlet();
      case "company": {
        const timeline = [...data.companyTimeline].sort(
          (a, b) => a.sortOrder - b.sortOrder || a.eventYear - b.eventYear || a.id - b.id,
        );

        return (
          <div className="space-y-8">
            <div className="bg-background-50 border border-background-200 rounded-lg p-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
                <div>
                  <h2 className="text-lg font-heading font-bold text-foreground-950">Nossa História / Empresa</h2>
                  <p className="text-foreground-500 text-sm mt-1">Título, história, ano de fundação, missão, visão e meta.</p>
                </div>
                <button onClick={() => openEdit("company")} className="flex items-center gap-1.5 bg-primary-500 hover:bg-primary-600 text-background-50 px-3 py-2 rounded-md text-xs font-label font-semibold transition-all cursor-pointer whitespace-nowrap">
                  <i className="ri-edit-line" /> Editar Empresa
                </button>
              </div>

              <div className="space-y-3">
                <div><strong className="text-sm text-foreground-950">Título:</strong> <span className="text-sm text-foreground-600">{data.companyInfo.historyTitle}</span></div>
                <div><strong className="text-sm text-foreground-950">Ano Fundação:</strong> <span className="text-sm text-foreground-600">{data.companyInfo.foundedYear}</span></div>
                <div><strong className="text-sm text-foreground-950">Nossa História:</strong><p className="text-sm text-foreground-600 mt-1 whitespace-pre-wrap">{data.companyInfo.historyText.substring(0, 300)}{data.companyInfo.historyText.length > 300 ? "..." : ""}</p></div>
                <div><strong className="text-sm text-foreground-950">Missão:</strong><p className="text-sm text-foreground-600">{data.companyInfo.mission}</p></div>
                <div><strong className="text-sm text-foreground-950">Visão:</strong><p className="text-sm text-foreground-600">{data.companyInfo.vision}</p></div>
                <div><strong className="text-sm text-foreground-950">Meta:</strong><p className="text-sm text-foreground-600">{data.companyInfo.meta}</p></div>
              </div>
            </div>

            <div className="bg-background-50 border border-background-200 rounded-lg p-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
                <div>
                  <h2 className="text-lg font-heading font-bold text-foreground-950">ConstruSete</h2>
                  <p className="text-foreground-500 text-sm mt-1">Logo, chamada, título, apresentação, link e visibilidade da seção ConstruSete.</p>
                </div>
                <button onClick={() => openEdit("companyConstruSete")} className="flex items-center gap-1.5 bg-primary-500 hover:bg-primary-600 text-background-50 px-3 py-2 rounded-md text-xs font-label font-semibold transition-all cursor-pointer whitespace-nowrap">
                  <i className="ri-edit-line" /> Editar ConstruSete
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-5 items-start">
                <div>
                  {data.companyInfo.construseteLogo ? (
                    <img src={data.companyInfo.construseteLogo} alt={data.companyInfo.construseteTitle || "ConstruSete"} className="w-full max-h-28 object-contain rounded-md border border-background-200 bg-background-100" />
                  ) : (
                    <div className="h-24 flex items-center justify-center border border-dashed border-background-300 rounded-md text-foreground-400 text-xs">Sem logo</div>
                  )}
                </div>

                <div className="space-y-2 min-w-0">
                  <div><strong className="text-sm text-foreground-950">Exibir:</strong> <span className={`text-sm font-semibold ${data.companyInfo.construseteActive ? "text-green-600" : "text-red-600"}`}>{data.companyInfo.construseteActive ? "Sim" : "Não"}</span></div>
                  <div><strong className="text-sm text-foreground-950">Chamada:</strong> <span className="text-sm text-foreground-600">{data.companyInfo.construseteBadge}</span></div>
                  <div><strong className="text-sm text-foreground-950">Título:</strong> <span className="text-sm text-foreground-600">{data.companyInfo.construseteTitle}</span></div>
                  <div><strong className="text-sm text-foreground-950">Texto:</strong><p className="text-sm text-foreground-600 mt-1 whitespace-pre-wrap">{data.companyInfo.construseteText.substring(0, 300)}{data.companyInfo.construseteText.length > 300 ? "..." : ""}</p></div>
                  {data.companyInfo.construseteUrl && <div><strong className="text-sm text-foreground-950">Link:</strong> <span className="text-sm text-foreground-600 break-all">{data.companyInfo.construseteUrl}</span></div>}
                </div>
              </div>
            </div>

            <div>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                <div>
                  <h2 className="text-lg font-heading font-bold text-foreground-950">Nossa Trajetória</h2>
                  <p className="text-foreground-500 text-sm mt-1">Gerencie os acontecimentos que aparecem na linha do tempo da página A Empresa.</p>
                </div>
                <button onClick={() => openEdit("companyTimeline")} className="flex items-center gap-1.5 bg-primary-500 hover:bg-primary-600 text-background-50 px-3 py-2 rounded-md text-xs font-label font-semibold transition-all cursor-pointer whitespace-nowrap">
                  <i className="ri-add-line" /> Adicionar Acontecimento
                </button>
              </div>

              <div className="space-y-3">
                {timeline.map((item: CompanyTimelineItem) => (
                  <div key={item.id} className="bg-background-50 border border-background-200 rounded-lg p-4 flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                    <div className="flex gap-4 min-w-0">
                      <div className="w-14 h-14 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center shrink-0 font-heading font-bold text-sm">
                        {item.eventYear}
                      </div>
                      <div className="min-w-0">
                        <p className="font-label font-semibold text-foreground-950 text-sm">{item.title}</p>
                        <p className="text-foreground-500 text-xs mt-1 leading-relaxed">{item.description}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-1 shrink-0 self-end sm:self-start">
                      <button onClick={() => openEdit("companyTimeline", item)} className="w-8 h-8 flex items-center justify-center text-foreground-500 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-all cursor-pointer" title="Editar acontecimento">
                        <i className="ri-edit-line text-sm" />
                      </button>
                      <button onClick={() => handleDelete("companyTimeline", item.id)} className="w-8 h-8 flex items-center justify-center text-foreground-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-all cursor-pointer" title="Excluir acontecimento">
                        <i className="ri-delete-bin-line text-sm" />
                      </button>
                    </div>
                  </div>
                ))}

                {timeline.length === 0 && (
                  <div className="bg-background-50 border border-dashed border-background-300 rounded-lg p-8 text-center">
                    <i className="ri-time-line text-3xl text-foreground-300 mb-3 block" />
                    <p className="text-foreground-500 text-sm font-label">Nenhum acontecimento cadastrado na trajetória.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      }
      case "promotions": return renderPromotions();
      case "testimonials": return renderList(data.testimonials, "testimonial", (t: Testimonial) => <div className="flex items-center gap-3">{t.photo ? <img src={t.photo} alt={t.name} className="w-10 h-10 rounded-full object-cover" /> : <div className="w-10 h-10 rounded-full bg-background-200 flex items-center justify-center text-foreground-400"><i className="ri-user-line" /></div>}<div><p className="font-label font-semibold text-foreground-950 text-sm">{t.name}</p><p className="text-foreground-500 text-xs truncate max-w-xs">{t.text}</p></div></div>);
      case "forms": return renderReceivedForms();
      case "vacancies": return renderVacancies();
      case "raffles": return renderRaffles();
      default: return null;
    }
  };

  if (authChecking) {
    return <div className="min-h-screen bg-background-100 flex items-center justify-center"><div className="flex items-center gap-2 text-foreground-500 text-sm font-label"><i className="ri-loader-4-line animate-spin text-lg" />Verificando sessão...</div></div>;
  }

  return (
    <div className="min-h-screen bg-background-100 flex">
      <aside className="w-64 bg-foreground-950 text-background-50 flex flex-col shrink-0 hidden lg:flex">
        <div className="p-4 border-b border-background-50/10"><img src={data.settings.logoUrl} alt="M&Z" className="h-10 w-auto object-contain" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} /><p className="text-background-400 text-xs mt-2 font-label">Painel Administrativo</p></div>
        <nav className="flex-1 py-3 overflow-y-auto">{(Object.keys(sectionLabels) as Section[]).map(key => <button key={key} onClick={() => setSection(key)} className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-label transition-all cursor-pointer whitespace-nowrap ${section === key ? "bg-primary-500 text-background-50 font-semibold" : "text-background-300 hover:text-background-50 hover:bg-background-50/5"}`}><div className="w-5 h-5 flex items-center justify-center"><i className={sectionIcons[key]} /></div>{sectionLabels[key]}</button>)}</nav>
        <div className="p-4 border-t border-background-50/10 space-y-2"><button onClick={handleLogout} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-background-400 hover:text-background-50 transition-all cursor-pointer rounded-md"><i className="ri-logout-box-line" /> Sair</button></div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-background-50 border-b border-background-200 px-4 md:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3"><select value={section} onChange={e => setSection(e.target.value as Section)} className="lg:hidden px-3 py-1.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-950 font-label focus:outline-none focus:border-primary-400">{(Object.keys(sectionLabels) as Section[]).map(key => <option key={key} value={key}>{sectionLabels[key]}</option>)}</select><h1 className="text-sm font-heading font-bold text-foreground-950 hidden sm:block">{sectionLabels[section]}</h1></div>
          <div className="flex items-center gap-2"><button onClick={handleReset} className="text-xs text-red-500 hover:text-red-700 font-label cursor-pointer whitespace-nowrap px-2 py-1"><i className="ri-refresh-line mr-1" />Recarregar Dados</button><a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-foreground-500 hover:text-primary-600 font-label cursor-pointer whitespace-nowrap"><i className="ri-external-link-line" /> Ver Site</a></div>
        </header>

        {msg && <div className={`fixed top-4 right-4 z-[300] ${msgType === "error" ? "bg-red-600" : "bg-green-600"} text-background-50 px-4 py-2 rounded-md text-sm font-label shadow-lg animate-fade-in`}>{msg}</div>}

        <main className="flex-1 p-4 md:p-6 overflow-y-auto"><div className="max-w-5xl mx-auto">{renderSection()}</div></main>
      </div>

      {editing && renderEditModal()}
      {raffleManagerId && renderRaffleManagerModal()}

      {confirmDialog && (
        <div className="fixed inset-0 z-[400] flex items-center justify-center p-4 bg-black/50" onClick={() => setConfirmDialog(null)}>
          <div className="w-full max-w-md bg-background-50 border border-background-200 rounded-xl p-5 shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="font-heading font-bold text-foreground-950 text-lg">{confirmDialog.title}</h3>
            <p className="text-foreground-600 text-sm mt-2">{confirmDialog.message}</p>
            <div className="flex justify-end gap-2 mt-5"><button type="button" onClick={() => setConfirmDialog(null)} className="px-4 py-2 text-sm font-label text-foreground-600 cursor-pointer">Cancelar</button><button type="button" onClick={async () => { const action = confirmDialog.onConfirm; setConfirmDialog(null); await action(); }} className={`${confirmDialog.danger ? "bg-red-600 hover:bg-red-700" : "bg-primary-500 hover:bg-primary-600"} text-background-50 px-4 py-2 rounded-md text-sm font-label font-semibold cursor-pointer`}>{confirmDialog.confirmText}</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
